import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { Brain, AlertTriangle, TrendingUp, TrendingDown, Activity, Target, BarChart3, Clock, Coins, ShieldAlert, Database, Crosshair, ClipboardList, Flame, Zap, ArrowRight, CheckCircle, XCircle, Eye, AlertOctagon, RotateCcw, Lock, HelpCircle, Info, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { calculateMissingFishIntelligence } from '@/utils/calculateMissingFishIntelligence';
import VisibilityGate from './VisibilityGate';
import { useReportVisibility } from '../contexts/ReportVisibilityContext';
import { formatMissingFishRange } from '@/utils/reportFormatters';
import GuardrailsOffDisclosure from './GuardrailsOffDisclosure';

// Glossary tooltip helper
const Term = ({ term, children }) => {
  const glossary = {
    'FCR': 'Feed Conversion Ratio — kg of feed needed to produce 1 kg of fish weight gain',
    'Jump Analysis': 'Detects sudden weight increases between samples that suggest fewer fish than reported',
    'Confidence': 'How reliable the estimate is based on data quality and detection strength',
    'Indicative': 'Preliminary estimate — use for planning only, not official records',
  };
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="underline decoration-dotted cursor-help">{children}</span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs">
          <p>{glossary[term] || term}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// Safe number formatter - guards against NaN/Infinity
const safeNum = (v, decimals = 0) => {
  if (v == null || !Number.isFinite(v)) return '—';
  return decimals > 0 ? v.toFixed(decimals) : v.toLocaleString();
};

const safePct = (v, decimals = 1) => {
  if (v == null || !Number.isFinite(v)) return '—%';
  return `${v.toFixed(decimals)}%`;
};

// ============================================================================
// UNDERSTANDING CONFIDENCE & DATA QUALITY — Permanent key for all reports
// ============================================================================

/**
 * ConfidenceDataQualityKey — Permanent explanatory panel that appears on every report
 * Explains how Confidence Score and Data Reliability are calculated using simple factors
 */
export const ConfidenceDataQualityKey = ({ dataQuality, finalDecision, ssot, collapsed = false }) => {
  const [isExpanded, setIsExpanded] = useState(!collapsed);
  const fd = finalDecision || {};
  
  // Extract scores
  const confidenceScore = fd.detectionStrengthScore ?? ssot?.detectionStrengthScore ?? 0;
  const dataReliabilityScore = dataQuality?.score ?? fd.dataQualityScore ?? 0;
  
  // Determine levels (High/Moderate/Low)
  const getConfidenceLevel = (score) => {
    if (score >= 70) return { level: 'High', color: 'text-emerald-700', bg: 'bg-emerald-100', border: 'border-emerald-300' };
    if (score >= 40) return { level: 'Moderate', color: 'text-amber-700', bg: 'bg-amber-100', border: 'border-amber-300' };
    return { level: 'Low', color: 'text-red-700', bg: 'bg-red-100', border: 'border-red-300' };
  };
  
  const getDataQualityLevel = (score) => {
    if (score >= 80) return { level: 'High', color: 'text-emerald-700', bg: 'bg-emerald-100', border: 'border-emerald-300' };
    if (score >= 60) return { level: 'Moderate', color: 'text-amber-700', bg: 'bg-amber-100', border: 'border-amber-300' };
    return { level: 'Low', color: 'text-red-700', bg: 'bg-red-100', border: 'border-red-300' };
  };
  
  const confidenceInfo = getConfidenceLevel(confidenceScore);
  const dataQualityInfo = getDataQualityLevel(dataReliabilityScore);
  
  return (
    <div className="bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-300 rounded-xl overflow-hidden" data-testid="confidence-data-quality-key">
      {/* Header - Always visible */}
      <button 
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-100/50 transition-colors"
        data-testid="confidence-key-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-600 rounded-lg">
            <HelpCircle className="w-5 h-5 text-white" />
          </div>
          <div className="text-left">
            <h4 className="text-sm font-bold text-slate-800">Understanding Confidence & Data Quality</h4>
            <p className="text-xs text-slate-500">How scores are calculated and what they mean</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Quick score badges */}
          <div className={`px-3 py-1 rounded-full text-xs font-semibold ${confidenceInfo.bg} ${confidenceInfo.color}`}>
            Confidence: {confidenceInfo.level}
          </div>
          <div className={`px-3 py-1 rounded-full text-xs font-semibold ${dataQualityInfo.bg} ${dataQualityInfo.color}`}>
            Data Quality: {dataQualityInfo.level}
          </div>
          {isExpanded ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
        </div>
      </button>
      
      {/* Expandable content */}
      {isExpanded && (
        <div className="px-4 pb-4 space-y-4 border-t border-slate-200">
          {/* Score Thresholds Legend */}
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Confidence Score Explanation */}
            <div className="bg-white rounded-lg border border-slate-200 p-4">
              <div className="flex items-center gap-2 mb-3">
                <Target className="w-5 h-5 text-blue-600" />
                <h5 className="text-sm font-bold text-slate-800">Confidence Score</h5>
                <span className={`ml-auto px-2 py-0.5 rounded text-xs font-bold ${confidenceInfo.bg} ${confidenceInfo.color}`}>
                  {confidenceScore}/100 ({confidenceInfo.level})
                </span>
              </div>
              
              <p className="text-xs text-slate-600 mb-3">
                Measures how confident we are in the detection signal. Based on:
              </p>
              
              <div className="space-y-2 mb-3">
                <div className="flex items-center gap-2 text-xs">
                  <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                  <span className="text-slate-700"><strong>Signal Strength</strong> — Magnitude of FCR deviation from expected</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                  <span className="text-slate-700"><strong>Sample Count</strong> — Number of weight samples available</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                  <span className="text-slate-700"><strong>Method Agreement</strong> — Consistency between FCR & Jump detection</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                  <span className="text-slate-700"><strong>Pattern Consistency</strong> — Stability of signal over time</span>
                </div>
              </div>
              
              {/* Thresholds */}
              <div className="bg-slate-50 rounded-lg p-3 space-y-1.5">
                <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-2">Score Thresholds</p>
                <div className="flex items-center gap-2">
                  <span className="w-16 text-xs font-bold text-emerald-700">70–100</span>
                  <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-[10px] font-semibold rounded">HIGH</span>
                  <span className="text-[10px] text-slate-500">— Strong signal, actionable for planning</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-16 text-xs font-bold text-amber-700">40–69</span>
                  <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-semibold rounded">MODERATE</span>
                  <span className="text-[10px] text-slate-500">— Indicative signal, requires validation</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-16 text-xs font-bold text-red-700">0–39</span>
                  <span className="px-2 py-0.5 bg-red-100 text-red-700 text-[10px] font-semibold rounded">LOW</span>
                  <span className="text-[10px] text-slate-500">— Weak signal, monitoring only</span>
                </div>
              </div>
            </div>
            
            {/* Data Reliability Explanation */}
            <div className="bg-white rounded-lg border border-slate-200 p-4">
              <div className="flex items-center gap-2 mb-3">
                <Database className="w-5 h-5 text-purple-600" />
                <h5 className="text-sm font-bold text-slate-800">Data Reliability</h5>
                <span className={`ml-auto px-2 py-0.5 rounded text-xs font-bold ${dataQualityInfo.bg} ${dataQualityInfo.color}`}>
                  {dataReliabilityScore}/100 ({dataQualityInfo.level})
                </span>
              </div>
              
              <p className="text-xs text-slate-600 mb-3">
                Measures the quality and completeness of input data. Based on:
              </p>
              
              <div className="space-y-2 mb-3">
                <div className="flex items-center gap-2 text-xs">
                  <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                  <span className="text-slate-700"><strong>Sample Frequency</strong> — How often weight samples were taken (40%)</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                  <span className="text-slate-700"><strong>Feed Continuity</strong> — Completeness of daily feed records (25%)</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                  <span className="text-slate-700"><strong>Mortality Reporting</strong> — Coverage of mortality data (20%)</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                  <span className="text-slate-700"><strong>Temperature Data</strong> — Availability of water temperature (15%)</span>
                </div>
              </div>
              
              {/* Thresholds */}
              <div className="bg-slate-50 rounded-lg p-3 space-y-1.5">
                <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-2">Score Thresholds</p>
                <div className="flex items-center gap-2">
                  <span className="w-16 text-xs font-bold text-emerald-700">80–100</span>
                  <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-[10px] font-semibold rounded">HIGH</span>
                  <span className="text-[10px] text-slate-500">— Comprehensive data, reliable analysis</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-16 text-xs font-bold text-amber-700">60–79</span>
                  <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-semibold rounded">MODERATE</span>
                  <span className="text-[10px] text-slate-500">— Adequate data, some gaps present</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-16 text-xs font-bold text-red-700">0–59</span>
                  <span className="px-2 py-0.5 bg-red-100 text-red-700 text-[10px] font-semibold rounded">LOW</span>
                  <span className="text-[10px] text-slate-500">— Limited data, results less certain</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Combined Interpretation Guide */}
          <div className="bg-white rounded-lg border border-slate-200 p-4">
            <div className="flex items-center gap-2 mb-3">
              <Info className="w-5 h-5 text-slate-600" />
              <h5 className="text-sm font-bold text-slate-800">How to Interpret Results</h5>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                <p className="text-xs font-bold text-emerald-800 mb-1">High Confidence + High Data Quality</p>
                <p className="text-[10px] text-emerald-700">Results are reliable for operational planning. Signal is strong and data supports the conclusion.</p>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-xs font-bold text-amber-800 mb-1">Moderate Confidence or Data Quality</p>
                <p className="text-[10px] text-amber-700">Results are indicative but require validation. Consider additional sampling before major decisions.</p>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-xs font-bold text-red-800 mb-1">Low Confidence or Data Quality</p>
                <p className="text-[10px] text-red-700">Results should be treated as early signals only. Do not use for stock record adjustments.</p>
              </div>
            </div>
          </div>
          
          {/* Important Note */}
          <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-lg p-3">
            <AlertTriangle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <p className="text-[10px] text-blue-800">
              <strong>Important:</strong> These scores are analytical indicators only. Stock record changes always require physical validation regardless of score levels. Results inform planning decisions but do not replace field verification.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// PLANNING ESTIMATE SUMMARY — Single headline answer for users
// ============================================================================

/**
 * PlanningEstimateSummary — Shows THE answer prominently
 * One number, confidence bar, source explanation. Details collapsed.
 *
 * ★ LOW-CONFIDENCE SUPPRESSION (SSOT enforcement):
 *   When confidenceMode === 'signal' OR detectionStrength === 'Indication Only'
 *   OR decisionContext.decisionClass === 'MONITOR_ONLY', the large primary
 *   count is HIDDEN and replaced with:
 *     "Indicative signal detected — estimate withheld due to insufficient confirmation"
 *   The raw number remains accessible under a collapsible "supporting context"
 *   block so analysts can inspect it without it dominating the report.
 */
export const PlanningEstimateSummary = ({ ssot, finalDecision, methodResolution, decisionContext = null, onToggleDetails, showDetails = false, guardrailsOff = false }) => {
  const fd = finalDecision || {};
  const hasMissing = fd.hasMissingFish || ssot?.hasMissingFish;
  const missingCount = ssot?.missingFishCount || fd.missingFishCount || 0;
  const missingPercent = ssot?.missingFishPercent || fd.missingFishPercent || 0;
  const confidenceScore = fd.detectionStrengthScore ?? ssot?.detectionStrengthScore ?? 0;
  const confidenceLabel = ssot?.confidenceLabel || fd.detectionStrengthLabel || ssot?.detectionStrength || 'Low';
  const primaryMethod = ssot?.primaryMethod || methodResolution?.activeMethod || 'fcr';
  const methodLabel = primaryMethod === 'jump' ? 'Jump Analysis' : 'FCR Analysis';
  // ★ PRD canonical range label — built in generateIntelligence.js (ssot.missingFishRangeLabel).
  //   Fallback to formatMissingFishRange(ssot) in case the SSOT is not yet enriched.
  const rangeInfo = formatMissingFishRange(ssot || {});
  const rangeLabel = ssot?.missingFishRangeLabel || rangeInfo.label;
  const isSnapshotMode = fd.confidenceMode === 'signal' && confidenceScore < 40;

  // ★ Low-confidence / MONITOR_ONLY gate — applies across both Spreadsheet & Growth vs Model reports.
  //   Tightened vs earlier iterations: the `confidenceScore < 40` trap only fires WHEN a
  //   missing-fish signal is actually present (hasMissing), so clean datasets with low
  //   sample-density are still allowed to render the "Stock Tracking Normal" block.
  //   Explicit signal flags (`confidenceMode==='signal'`, `Indication Only`, `Uncertain`,
  //   MONITOR_ONLY) always trigger the withhold copy regardless of count — this closes
  //   the iter_195/iter_196 gap where a seed with MONITOR_ONLY + within-margin count
  //   short-circuited past the withhold branch.
  const decisionClass = decisionContext?.decisionClass || null;
  const isIndicationOnly = fd.confidenceMode === 'signal'
    || confidenceLabel === 'Indication Only'
    || confidenceLabel === 'Uncertain'
    || decisionClass === 'MONITOR_ONLY'
    || (confidenceScore < 40 && hasMissing);
  const [lcExpanded, setLcExpanded] = useState(false);
  // Auto-expand the raw supporting figure when global Guardrails are OFF —
  // the user has already opted in to seeing low-confidence estimates, so a
  // hidden-by-default behind-a-button hides the very thing they switched
  // the toggle to see.
  const lcEffectiveExpanded = lcExpanded || guardrailsOff;
  
  // Snapshot mode banner - only 1 sample, no quantitative estimate possible
  if (isSnapshotMode && hasMissing) {
    return (
      <div className="bg-gradient-to-r from-slate-100 to-slate-50 border-2 border-slate-300 rounded-xl p-5 mb-4" data-testid="planning-estimate-snapshot">
        <div className="flex items-center gap-3 mb-3">
          <AlertTriangle className="w-6 h-6 text-slate-500" />
          <p className="text-lg font-bold text-slate-700">Snapshot Mode</p>
        </div>
        <p className="text-sm text-slate-600 mb-2">
          <strong>Deviation signal detected</strong>, but quantitative estimates require more data points.
        </p>
        <p className="text-xs text-slate-500">
          Add 2+ sample weights over time to unlock fish count estimates and confidence scoring.
        </p>
      </div>
    );
  }

  // ★ H4 — CANONICAL ORDERING FIX (iter_195/iter_196): the `isIndicationOnly` branch
  //   MUST be evaluated BEFORE the `!hasMissing` early-return. A record can carry
  //   an explicit indicative signal (Detection Strength 'Indication Only (0/100)' +
  //   decisionClass MONITOR_ONLY) while its computed missing-fish band rounds to
  //   'Within margin' (missingCount === 0). Previously that sequence returned the
  //   "Stock Tracking Normal" block and masked the real uncertainty. Now the PRD
  //   withhold copy ('Indicative signal detected — estimate withheld due to
  //   insufficient confirmation') wins whenever the SSOT flags indication-only.
  if (isIndicationOnly) {
    // Guardrails-OFF override (PRD): when the user has explicitly flipped
    // the Indicative Report Mode toggle ON, we MUST show the calculated
    // missing-fish estimate even though the underlying confidence is low /
    // MONITOR_ONLY / sample-sparse. Hiding it behind "estimate withheld"
    // copy would defeat the toggle. Estimate stays visually downgraded
    // (amber, ~ prefix, "Indicative — Reference Only" pill) and carries
    // the full guardrails-off disclosure banner so the user can never
    // mistake it for a confirmed finding.
    if (guardrailsOff && missingCount > 0) {
      return (
        <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-400 rounded-xl p-5 mb-4" data-testid="planning-estimate-indication-only-guardrails-off">
          <GuardrailsOffDisclosure variant="full" testId="planning-estimate-guardrails-off-disclosure" />

          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <p className="text-xs text-amber-700 uppercase tracking-wider flex items-center gap-1">
                Estimated missing fish (Indicative)
                <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300">
                  Reference only
                </span>
              </p>
              <p className="text-2xl font-bold text-rose-700 mt-0.5" data-testid="planning-estimate-indicative-count">
                ~{missingCount.toLocaleString()}
              </p>
              <p className="text-xs text-amber-800 mt-0.5">
                ≈ <span className="font-semibold">{Number(missingPercent).toFixed(1)}%</span> of declared count · range {rangeLabel}
              </p>
            </div>
            <div>
              <p className="text-xs text-amber-700 uppercase tracking-wider">Confidence</p>
              <p className="text-sm font-semibold text-amber-800 mt-0.5">
                {confidenceLabel} ({Math.round(confidenceScore)}/100){decisionClass === 'MONITOR_ONLY' ? ' · MONITOR_ONLY' : ''}
              </p>
              <div className="h-2 bg-amber-100 rounded-full overflow-hidden mt-1">
                <div className="h-full bg-amber-400 rounded-full" style={{ width: `${Math.min(100, confidenceScore)}%` }} />
              </div>
              <p className="text-[11px] text-amber-700 mt-1.5">
                Based on <span className="font-semibold">{methodLabel}</span>. Validate with additional sampling before any operational stock adjustment.
              </p>
            </div>
          </div>
        </div>
      );
    }

    // Default (Guardrails ON): the existing PRD withhold-copy block.
    return (
      <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-300 rounded-xl p-5 mb-4" data-testid="planning-estimate-indication-only">
        <div className="flex items-start gap-3 mb-2">
          <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-base font-bold text-amber-900" data-testid="planning-estimate-indication-title">
              Indicative signal detected — estimate withheld due to insufficient confirmation
            </p>
            <p className="text-xs text-amber-800 mt-1">
              Based on <span className="font-semibold">{methodLabel}</span> with <span className="font-semibold">{confidenceLabel}</span> confidence{decisionClass === 'MONITOR_ONLY' ? ' (MONITOR_ONLY)' : ''}. Collect additional samples or validate feed records before using any figure for planning.
            </p>
          </div>
        </div>

        {/* Confidence bar — kept so reviewers see the strength level */}
        <div className="mb-2">
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="text-amber-700">Confidence</span>
            <span className="text-amber-800 font-semibold">{confidenceLabel} ({Math.round(confidenceScore)}/100)</span>
          </div>
          <div className="h-2 bg-amber-100 rounded-full overflow-hidden">
            <div className="h-full bg-amber-400 rounded-full transition-all" style={{ width: `${Math.min(100, confidenceScore)}%` }} />
          </div>
        </div>

        {/* Supporting context — collapsible. Raw estimate is secondary, not primary.
            When Guardrails are OFF globally, this opens by default and the
            raw block carries the same disclosure banner that the Jump and
            FCR panels use. */}
        <button
          type="button"
          onClick={() => setLcExpanded(v => !v)}
          className="mt-2 flex items-center gap-1 text-xs text-amber-800 hover:text-amber-900 font-medium"
          data-testid="planning-estimate-indication-toggle"
          disabled={guardrailsOff}
          title={guardrailsOff ? 'Auto-expanded because Guardrails are OFF' : undefined}
        >
          {lcEffectiveExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          {lcEffectiveExpanded ? 'Hide raw supporting figure' : 'Show raw supporting figure (not decision-grade)'}
        </button>
        {lcEffectiveExpanded && (
          <div className="mt-2 space-y-2" data-testid="planning-estimate-indication-raw">
            {guardrailsOff && (
              <GuardrailsOffDisclosure variant="short" testId="planning-raw-guardrails-off-disclosure" />
            )}
            <div className="p-2.5 bg-white/60 border border-amber-200 rounded-md text-xs text-amber-900">
              <p>
                Raw model estimate: <span className="font-semibold">~{missingCount.toLocaleString()}</span> fish (<span className="font-semibold">~{Number(missingPercent).toFixed(1)}%</span>) — do not cite as a finding or use for stock adjustments.
              </p>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ★ Clean dataset — no signal, adequate confidence: show the "Stock Tracking Normal"
  //   block. This branch now runs AFTER the isIndicationOnly check so a record with
  //   MONITOR_ONLY + within-margin count renders the withhold copy (not the green
  //   "all clear" block).
  if (!hasMissing || missingCount <= 0) {
    return (
      <div className="bg-gradient-to-r from-emerald-50 to-green-50 border-2 border-emerald-300 rounded-xl p-5 mb-4" data-testid="planning-estimate-normal">
        <div className="flex items-center gap-3">
          <CheckCircle className="w-8 h-8 text-emerald-600" />
          <div>
            <p className="text-lg font-bold text-emerald-800">Stock Tracking Normal</p>
            <p className="text-sm text-emerald-700">No stock adjustment indicated. Continue routine monitoring.</p>
          </div>
        </div>
      </div>
    );
  }

  // Confidence bar segments (0-100 scale)
  const barColor = confidenceScore >= 70 ? 'bg-emerald-500' : confidenceScore >= 40 ? 'bg-amber-500' : 'bg-red-400';
  const labelColor = confidenceScore >= 70 ? 'text-emerald-700' : confidenceScore >= 40 ? 'text-amber-700' : 'text-red-700';
  const bgGradient = confidenceScore >= 70 ? 'from-emerald-50 to-cyan-50 border-emerald-300' : confidenceScore >= 40 ? 'from-amber-50 to-yellow-50 border-amber-300' : 'from-red-50 to-orange-50 border-red-300';

  return (
    <div className={`bg-gradient-to-r ${bgGradient} border-2 rounded-xl p-5 mb-4`} data-testid="planning-estimate-summary">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Planning Estimate</p>
      
      {/* Headline — PRD canonical range format (H1/H7) */}
      <div className="flex items-baseline gap-3 mb-3 flex-wrap">
        <span className="text-4xl font-bold text-slate-900" data-testid="planning-estimate-number">
          {rangeLabel}
        </span>
        <span className="text-sm text-slate-600">
          ({missingPercent.toFixed(1)}% · midpoint ~{rangeInfo.midpointLabel || missingCount.toLocaleString()})
        </span>
      </div>

      {/* Confidence Bar */}
      <div className="mb-3">
        <div className="flex items-center justify-between text-xs mb-1">
          <span className="text-slate-500">Confidence</span>
          <span className={`font-semibold ${labelColor}`}>{confidenceLabel}</span>
        </div>
        <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
          <div className={`h-full ${barColor} rounded-full transition-all`} style={{ width: `${Math.min(100, confidenceScore)}%` }} />
        </div>
        <div className="flex justify-between text-[9px] text-slate-400 mt-1">
          <span>🔴 Low</span>
          <span>🟡 Moderate</span>
          <span>🟢 High</span>
        </div>
      </div>

      {/* Source */}
      <p className="text-sm text-slate-600">
        Based on <span className="font-semibold">{methodLabel}</span> (primary method)
      </p>

      {/* Method Difference Explanation - shows when both methods available with different results */}
      {methodResolution?.jumpAvailable && methodResolution?.fcrAvailable && 
       methodResolution?.jumpFishNumber !== methodResolution?.fcrFishNumber && (
        <div className="mt-3 bg-slate-100 rounded-lg p-3 text-xs text-slate-600" data-testid="method-difference-note">
          <p className="font-medium text-slate-700 mb-1">Why do FCR and Jump estimates differ?</p>
          <p><Term term="FCR"><strong>FCR</strong></Term> uses feed consumption data — more reliable when feed records are accurate.</p>
          <p><Term term="Jump Analysis"><strong>Jump</strong></Term> detects sudden weight changes — useful when feed data is incomplete.</p>
        </div>
      )}

      {/* Toggle Details */}
      {onToggleDetails && (
        <button 
          onClick={onToggleDetails}
          className="mt-3 flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 transition-colors"
          data-testid="toggle-method-details"
        >
          {showDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          {showDetails ? 'Hide method details' : 'View method details'}
        </button>
      )}
    </div>
  );
};

// ============================================================================
// DECISION CONTEXT PANEL — Structured decision support with risk control
// ============================================================================

/**
 * FCROutlierCallout — Diagnostic panel listing inter-sample periods that were
 * excluded from analysis due to implausible observed FCR values.
 *
 * - Input: `ssot.fcrOutliers` produced by detectFCROutliers() in /utils/fcrOutliers.js
 * - Tone: concise, diagnostic, not alarmist. Explains that confidence was
 *   downgraded because of data-quality issues, not because of a detected
 *   stock event.
 * - Hidden when no implausible/negative outliers are present (high_outlier on
 *   its own is not surfaced because those periods are kept in the analysis).
 */
export const FCROutlierCallout = ({ ssot }) => {
  const outliers = ssot?.fcrOutliers;
  if (!outliers || !outliers.hasExtremeFCR) return null;

  const excluded = (outliers.details || []).filter(
    d => d.classification === 'implausible_low'
      || d.classification === 'implausible_high'
      || d.classification === 'negative'
  );
  if (excluded.length === 0) return null;

  const reasonLabel = {
    implausible_low: 'Implausibly low FCR',
    implausible_high: 'Implausibly high FCR',
    negative: 'Biomass decreased while feed was given',
  };

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return String(iso);
    }
  };

  return (
    <div className="rounded-lg border border-slate-300 bg-slate-50 p-4 mb-4" data-testid="fcr-outlier-callout">
      <div className="flex items-start gap-3">
        <Info className="w-5 h-5 text-slate-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="flex items-center justify-between mb-1">
            <h4 className="text-sm font-semibold text-slate-800">Excluded from analysis</h4>
            <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-slate-200 text-slate-700" data-testid="fcr-outlier-count">
              {excluded.length} period{excluded.length !== 1 ? 's' : ''}
            </span>
          </div>
          <p className="text-xs text-slate-600 leading-relaxed mb-2">
            These inter-sample periods were excluded from interval-level FCR analysis because the detected values are not biologically plausible — typically a data-entry issue (duplicated feed, swapped weights, or unreported mortality/harvest). Overall confidence was downgraded because of these data-quality issues; this is not a detected stock event.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-200 text-slate-500">
                  <th className="text-left py-1 px-2 font-medium">Period</th>
                  <th className="text-right py-1 px-2 font-medium">Detected FCR</th>
                  <th className="text-right py-1 px-2 font-medium">Feed</th>
                  <th className="text-right py-1 px-2 font-medium">Biomass Δ</th>
                  <th className="text-left py-1 px-2 font-medium">Reason</th>
                </tr>
              </thead>
              <tbody>
                {excluded.map((d, i) => (
                  <tr key={`${d.fromIndex}-${d.toIndex}-${i}`} className="border-b border-slate-100" data-testid={`fcr-outlier-row-${i}`}>
                    <td className="py-1 px-2 text-slate-700 whitespace-nowrap">
                      {fmtDate(d.fromDate)} → {fmtDate(d.toDate)}
                    </td>
                    <td className="py-1 px-2 text-right text-slate-800 font-medium">
                      {d.fcr != null ? d.fcr.toFixed(2) : '—'}
                    </td>
                    <td className="py-1 px-2 text-right text-slate-700">
                      {d.feedKg.toLocaleString()} kg
                    </td>
                    <td className={`py-1 px-2 text-right ${d.biomassGainKg <= 0 ? 'text-amber-700 font-medium' : 'text-slate-700'}`}>
                      {d.biomassGainKg > 0 ? '+' : ''}{d.biomassGainKg.toLocaleString()} kg
                    </td>
                    <td className="py-1 px-2 text-slate-600">
                      {reasonLabel[d.classification] || d.classification}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-slate-500 italic mt-2">
            Tip: review the source spreadsheet for these date ranges. Correcting the data and re-uploading will restore full confidence.
          </p>
        </div>
      </div>
    </div>
  );
};

/**
 * DecisionContextPanel — Displays structured decision support at TOP of report.
 * Injects into existing Growth Intelligence Summary without redesign.
 * 
 * GLOBAL POLICY: Results inform planning, feeding, and operational review.
 * Results must NEVER directly overwrite official stock records.
 * 
 * MISUSE RESISTANCE:
 * - Financial outputs do not visually dominate decision class
 * - VALIDATION_REQUIRED appears visually stronger than ACTIONABLE
 * - Downgrade explanations are always visible when active
 * - Uncertainty wording prevents overconfidence
 */
export const DecisionContextPanel = ({ decisionContext, ssot, finalDecision, growthFit = null, analyticalSignals = null, fcrBasedDetection = null }) => {
  if (!decisionContext) return null;
  const fd = finalDecision || {};

  const { 
    dataReliability, 
    decisionClass, 
    decisionReason,
    downgradeExplanation,
    stockRecordPolicy,
    validation 
  } = decisionContext;

  // ★ ALL interpretive labels from finalDecision — no local derivation
  // Gate financial context: only show when missing fish narrative is active
  const financialConfidenceLevel = fd.hasMissingFish ? (fd.financialContextLevel || 'not_actionable') : 'not_actionable';
  const financialImpactEur = fd.hasMissingFish ? (ssot?.financialImpactEur || 0) : 0;
  const wastedFeedKg = fd.hasMissingFish ? (ssot?.wastedFeedKg || 0) : 0;
  const financialConfidenceReason = fd.hasMissingFish ? (fd.financialContextReason || '') : '';

  // Decision class colors and icons — Standardized 3-state system
  const classStyles = {
    'ACTIONABLE': { 
      bg: 'bg-slate-800/60', 
      border: 'border-cyan-600', 
      text: 'text-cyan-400', 
      headerBg: 'bg-cyan-900/40',
      icon: CheckCircle, 
      label: 'ACTIONABLE' 
    },
    'VALIDATION_REQUIRED': { 
      bg: 'bg-red-950/40', 
      border: 'border-red-500 border-2', 
      text: 'text-red-400',
      headerBg: 'bg-red-900/50',
      icon: AlertOctagon, 
      label: 'VALIDATION REQUIRED' 
    },
    'MONITOR_ONLY': { 
      bg: 'bg-slate-800/60', 
      border: 'border-amber-500', 
      text: 'text-amber-400',
      headerBg: 'bg-amber-900/40',
      icon: Eye, 
      label: 'MONITOR ONLY' 
    },
  };
  const style = classStyles[decisionClass] || classStyles['MONITOR_ONLY'];
  const DecisionIcon = style.icon;

  // PRD UX-fix #2 (Strong Signal label): when MONITOR_ONLY classification is
  // active but the underlying evidence is actually strong (persistent
  // below-model deviation + meaningful FCR gap + Moderate+ detection
  // confidence), surface a "Validation Required — Strong Signal" headline so
  // the user is not under-warned. This is a DISPLAY-ONLY change — the SSOT
  // class, calculations, thresholds and confidence scoring are unchanged.
  const detectionStrengthForStrong = (ssot?.detectionStrength || '').toString().toLowerCase();
  const detectionStrengthIsModerateOrHigher = (
    detectionStrengthForStrong === 'moderate' ||
    detectionStrengthForStrong === 'high' ||
    detectionStrengthForStrong.includes('moderate') ||
    detectionStrengthForStrong.includes('high')
  );
  const persistenceStreak = analyticalSignals?.streak?.maxStreak || 0;
  const fcrReportedRaw = ssot?.reportedFCR ?? fcrBasedDetection?.reportedClosingFCR ?? null;
  const fcrModelRaw = ssot?.modelFCR ?? fcrBasedDetection?.modelFCR ?? null;
  const fcrGapPct = (typeof fcrReportedRaw === 'number' && typeof fcrModelRaw === 'number' && fcrModelRaw > 0)
    ? Math.abs(fcrReportedRaw - fcrModelRaw) / fcrModelRaw
    : null;
  const isStrongSignalUpgrade = !!(
    decisionClass === 'MONITOR_ONLY' &&
    persistenceStreak >= 3 &&
    fcrGapPct != null && fcrGapPct >= 0.15 &&
    detectionStrengthIsModerateOrHigher
  );
  const headerLabel = isStrongSignalUpgrade ? 'VALIDATION REQUIRED — STRONG SIGNAL' : style.label;

  // Detection type styling — ★ from finalDecision.detectionType only
  const detectionType = fd.detectionType || 'Normal';
  const detectionStyles = {
    'Missing Fish': { color: 'text-red-400', bg: 'bg-red-900/20' },
    'missing_fish': { color: 'text-red-400', bg: 'bg-red-900/20' },
    'Growth Suppression': { color: 'text-amber-400', bg: 'bg-amber-900/20' },
    'growth_suppression': { color: 'text-amber-400', bg: 'bg-amber-900/20' },
    'Extra Fish': { color: 'text-blue-400', bg: 'bg-blue-900/20' },
    'extra_fish': { color: 'text-blue-400', bg: 'bg-blue-900/20' },
    'Normal': { color: 'text-green-400', bg: 'bg-green-900/20' },
    'normal': { color: 'text-green-400', bg: 'bg-green-900/20' },
    'Below Model Performance': { color: 'text-blue-400', bg: 'bg-blue-900/20' },
    'below_model': { color: 'text-blue-400', bg: 'bg-blue-900/20' },
    'Deviation Signal': { color: 'text-amber-400', bg: 'bg-amber-900/20' },
  };
  const detStyle = detectionStyles[fd.detectionLabel || detectionType] || detectionStyles['Normal'];

  // Reliability classification colors
  const reliabilityClassColors = {
    strong: 'text-green-400',
    moderate: 'text-amber-400',
    limited: 'text-orange-400',
    insufficient: 'text-red-400',
  };
  const classification = dataReliability?.classification || {};

  // Reliability driver status colors
  const driverStatusColors = {
    good: 'text-green-400',
    moderate: 'text-amber-400',
    low: 'text-red-400',
  };

  // Check if downgrades are active
  const hasDowngrades = downgradeExplanation != null;

  return (
    <div className={`${style.bg} border ${style.border} rounded-lg overflow-hidden mb-4`} data-testid="decision-context-panel">
      {/* Decision Class Header — visually prominent */}
      <VisibilityGate sectionKey="dcp.header" label="Decision Class Header">
        <div className={`${isStrongSignalUpgrade ? 'bg-amber-900/50' : style.headerBg} px-4 py-2.5 flex items-center justify-between`}>
          <div className="flex items-center gap-2">
            <DecisionIcon className={`w-5 h-5 ${isStrongSignalUpgrade ? 'text-amber-300' : style.text}`} />
            <span className={`text-sm font-bold ${isStrongSignalUpgrade ? 'text-amber-300' : style.text} tracking-wide`} data-testid="decision-class-header-label">{headerLabel}</span>
          </div>
          <span className="text-[10px] text-slate-400 max-w-[45%] text-right leading-tight">{isStrongSignalUpgrade ? 'Persistent below-model deviation with meaningful FCR gap — validation required.' : decisionReason}</span>
        </div>
      </VisibilityGate>

      {/* Downgrade explanation banner (if active) */}
      {hasDowngrades && (
        <div className="bg-amber-900/30 border-b border-amber-700 px-4 py-2">
          <p className="text-[10px] text-amber-300 font-medium">
            ⚠ {downgradeExplanation}
          </p>
        </div>
      )}

      {/* Main content */}
      <div className="p-4">
        {/* Grid: Detection | Reliability | Financial | Actions */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          {/* 1. DETECTION — ★ ALL from finalDecision */}
          <VisibilityGate sectionKey="dcp.detection" label="Detection Box">
            {(() => {
              // PRD wording-fix: never label the detection panel as "Normal"
              // when the same SSOT bundle reports a Growth-Fit / FCR / jump
              // discrepancy estimate, indication-only, monitor-only, or
              // low reliability. Falling back to a calibrated label here
              // keeps the headline panel consistent with the audit footer
              // and prevents the contradictions reported in production
              // (e.g. Growth-Fit deviation 23,965 next to "Detection: Normal").
              const explicit = fd.detectionLabel || fd.detectionType;
              const hasDiscrepancyEvidence = !!(
                fd.hasMissingFish ||
                ssot?.hasMissingFish ||
                (typeof ssot?.missingFishCount === 'number' && ssot.missingFishCount > 0) ||
                ssot?.fcrConflict ||
                ssot?.confidenceMode === 'signal' ||
                ssot?.confidenceMode === 'evidence' ||
                ssot?.growthFitHasDeviation ||
                (growthFit && (growthFit.detected || (typeof growthFit.estimatedMissing === 'number' && growthFit.estimatedMissing > 0))) ||
                decisionClass === 'MONITOR_ONLY' ||
                (dataReliability?.score != null && dataReliability.score < 50)
              );
              const safeExplicit = (explicit || '').toLowerCase() === 'normal' && hasDiscrepancyEvidence
                ? 'Indicative'
                : explicit;
              const displayLabel = safeExplicit || (hasDiscrepancyEvidence ? 'Indicative' : 'Normal');
              return (
                <div className={`${detStyle.bg} rounded-lg p-2.5`}>
                  <p className="text-[9px] text-slate-400 uppercase tracking-wider mb-1">Detection</p>
                  <p className={`font-bold ${detStyle.color}`}>{displayLabel}</p>
                  <p className="text-slate-300 mt-0.5">
                    {fd.detectionStrengthLabel} {fd.detectionProbability ? `(${fd.detectionProbability}%)` : ''}
                  </p>
                </div>
              );
            })()}
          </VisibilityGate>

          {/* 2. DATA RELIABILITY (with classification) */}
          <VisibilityGate sectionKey="dcp.data_reliability" label="Data Reliability Box">
            <div className="bg-slate-800/50 rounded-lg p-2.5">
              <p className="text-[9px] text-slate-400 uppercase tracking-wider mb-1">Data Reliability</p>
              {/* Score + Classification label */}
              <div className="flex items-baseline gap-2">
                <span className={`font-bold ${reliabilityClassColors[classification.tier] || 'text-slate-400'}`}>
                  {dataReliability?.score}/100
                </span>
                <span className={`text-[9px] ${reliabilityClassColors[classification.tier] || 'text-slate-400'}`}>
                  {classification.label || dataReliability?.label}
                </span>
              </div>
              {/* Reliability drivers (compact) */}
              {dataReliability?.drivers?.length > 0 && (
                <div className="mt-1.5 space-y-0.5">
                  {dataReliability.drivers.slice(0, 3).map((driver, i) => (
                    <div key={i} className="flex items-center justify-between text-[9px]">
                      <span className="text-slate-500">{driver.factor}</span>
                      <span className={driverStatusColors[driver.status] || 'text-slate-400'}>{driver.detail}</span>
                    </div>
                  ))}
                </div>
              )}
              {dataReliability?.sensitivityNote && (
                <p className="text-[9px] text-amber-400 mt-1.5 leading-tight border-t border-slate-700 pt-1">{dataReliability.sensitivityNote}</p>
              )}
            </div>
          </VisibilityGate>

          {/* 3. FINANCIAL CONTEXT — driven by SSOT financialConfidenceLevel */}
          <VisibilityGate sectionKey="dcp.financial_context" label="Financial Context Box">
            <div className={`bg-slate-800/50 rounded-lg p-2.5 ${financialConfidenceLevel !== 'confirmed' ? 'opacity-80' : ''}`}>
              <p className="text-[9px] text-slate-400 uppercase tracking-wider mb-1">Financial Context</p>
              {/* ★ Use financialConfidenceLevel from SSOT — NEVER derive locally */}
              {financialConfidenceLevel === 'not_actionable' ? (
                <>
                  <p className="text-slate-500 italic text-[10px]">Insufficient data for financial estimate</p>
                  {financialConfidenceReason && (
                    <p className="text-[9px] text-slate-500 mt-1">{financialConfidenceReason}</p>
                  )}
                </>
              ) : financialConfidenceLevel === 'indicative' ? (
                <>
                  <p className="text-[9px] text-amber-400 font-semibold mb-1 bg-amber-900/30 px-1.5 py-0.5 rounded">
                    Indicative
                  </p>
                  {financialImpactEur > 0 ? (
                    <>
                      <p className="text-[9px] text-slate-500 mb-0.5">Indicative exposure</p>
                      <p className="font-bold text-amber-400/80">
                        ~{financialImpactEur.toLocaleString()} EUR
                      </p>
                    </>
                  ) : (
                    <p className="font-bold text-green-400/80">0 EUR</p>
                  )}
                  {wastedFeedKg > 0 && (
                    <p className="text-[10px] text-slate-400 mt-1 leading-tight">
                      Based on {wastedFeedKg.toLocaleString()} kg wasted feed
                    </p>
                  )}
                  {financialConfidenceReason && (
                    <p className="text-[9px] text-amber-500 mt-1 italic">{financialConfidenceReason}</p>
                  )}
                </>
              ) : (
                // confirmed
                <>
                  {financialImpactEur > 0 ? (
                    <>
                      <p className="text-[9px] text-slate-500 mb-0.5">Confirmed exposure</p>
                      <p className="font-bold text-red-400">
                        {financialImpactEur.toLocaleString()} EUR
                      </p>
                    </>
                  ) : (
                    <p className="font-bold text-green-400/80">0 EUR</p>
                  )}
                  {wastedFeedKg > 0 && (
                    <p className="text-[10px] text-slate-400 mt-1 leading-tight">
                      Based on {wastedFeedKg.toLocaleString()} kg wasted feed
                    </p>
                  )}
                </>
              )}
            </div>
          </VisibilityGate>

          {/* 4. ACTIONS (with PRIMARY ACTION prominent) */}
          <VisibilityGate sectionKey="dcp.actions" label="Actions Box">
            <div className="bg-slate-800/50 rounded-lg p-2.5">
              <p className="text-[9px] text-slate-400 uppercase tracking-wider mb-1">Actions</p>
              {/* PRIMARY ACTION */}
              {fd.actions?.primary && (
                <div className="flex items-start gap-1.5 mb-2 pb-1.5 border-b border-slate-700">
                  <ArrowRight className="w-3.5 h-3.5 text-cyan-400 mt-0.5 shrink-0" />
                  <span className="text-slate-200 leading-tight text-[10px] font-medium">{fd.actions.primary.text}</span>
                </div>
              )}
              {/* Secondary actions */}
              {fd.actions?.secondary?.length > 0 && (
                <ul className="space-y-1">
                  {fd.actions.secondary.map((action, i) => (
                    <li key={i} className="flex items-start gap-1.5">
                      {action.reversibility === 'REVERSIBLE' ? (
                        <RotateCcw className="w-2.5 h-2.5 text-green-500/70 mt-0.5 shrink-0" />
                      ) : (
                        <Lock className="w-2.5 h-2.5 text-red-500/70 mt-0.5 shrink-0" />
                      )}
                      <span className="text-slate-400 leading-tight text-[9px]">{action.text}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </VisibilityGate>
        </div>

        {/* Stock Record Policy Banner (GLOBAL — always visible) */}
        <VisibilityGate sectionKey="dcp.stock_record_policy" label="Stock Record Policy">
          <div className="mt-3 bg-slate-900/60 border border-slate-600 rounded-lg p-2.5 flex items-start gap-2">
            <Lock className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-[10px] font-semibold text-slate-300">{stockRecordPolicy?.message || 'Do NOT adjust official stock records based on analytical results alone.'}</p>
              <p className="text-[9px] text-slate-500 mt-0.5">{stockRecordPolicy?.detail || 'Results inform planning, feeding, and biomass interpretation. Stock record changes require physical validation.'}</p>
            </div>
          </div>
        </VisibilityGate>

        {/* Validation Banner (if required) */}
        <VisibilityGate sectionKey="dcp.validation_banner" label="Validation Required Banner">
          {validation?.required && (
            <div className="mt-2 bg-red-900/30 border border-red-700 rounded-lg p-2.5 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-semibold text-red-400">Field Validation Required</p>
                <p className="text-[10px] text-slate-300 mt-0.5">
                  Triggers: {validation.triggers.join(' · ')}
                </p>
                <p className="text-[10px] text-slate-400 mt-0.5">
                  Examples: {validation.examples.join(' · ')}
                </p>
              </div>
            </div>
          )}
        </VisibilityGate>

        {/* Report Audit Footer — traceability for isolated outputs */}
        <VisibilityGate sectionKey="dcp.audit_footer" label="Audit Footer">
          <div className="mt-3 pt-2 border-t border-slate-700 flex flex-wrap items-center justify-between text-[8px] text-slate-500 font-mono">
            <span>
              Detection:{' '}
              {(() => {
                // PRD wording-fix: never label detection as "Normal" while
                // the same audit footer reports a Growth-Fit / FCR / jump
                // discrepancy estimate or low reliability — that's the
                // contradiction reported on the deployed app. We surface a
                // safer "Indicative" label whenever any deviation evidence
                // exists in the same SSOT bundle.
                const hasDiscrepancyEvidence = !!(
                  fd.hasMissingFish ||
                  ssot?.hasMissingFish ||
                  (typeof ssot?.missingFishCount === 'number' && ssot.missingFishCount > 0) ||
                  ssot?.fcrConflict ||
                  ssot?.confidenceMode === 'signal' ||
                  ssot?.confidenceMode === 'evidence' ||
                  ssot?.growthFitHasDeviation ||
                  (growthFit && (growthFit.detected || (typeof growthFit.estimatedMissing === 'number' && growthFit.estimatedMissing > 0))) ||
                  decisionClass === 'MONITOR_ONLY' ||
                  (dataReliability?.score != null && dataReliability.score < 50)
                );
                const explicit = fd.detectionLabel || fd.detectionType;
                if (explicit) {
                  // If the upstream label is bare "Normal" but evidence of a
                  // discrepancy exists, override to "Indicative" so the audit
                  // footer cannot contradict the body of the report.
                  const safe = (explicit || '').toLowerCase() === 'normal' && hasDiscrepancyEvidence
                    ? 'Indicative'
                    : explicit;
                  return `${safe} (${fd.detectionStrengthLabel || 'N/A'})`;
                }
                const label = hasDiscrepancyEvidence ? 'Indicative' : 'Normal';
                return `${label} (${fd.detectionStrengthLabel || 'N/A'})`;
              })()}
            </span>
            <span>Reliability: {dataReliability?.score}/100</span>
            <span>Class: {decisionClass}</span>
            <span>Generated: {new Date().toISOString().slice(0, 16).replace('T', ' ')}</span>
          </div>
        </VisibilityGate>
      </div>
    </div>
  );
};

// ★ calculateMissingFishIntelligence extracted to @/utils/calculateMissingFishIntelligence.js


/**
 * Sample Jump Analysis Panel — the key UI for fish loss estimation
 * Includes assumed fish number control, apply suggested button, and summary metrics
 * When FCR is the primary detection mode (activeMethod === 'fcr'), this panel
 * shows a disabled "No jump detected" state with no editable input.
 */
const SampleJumpAnalysisPanel = ({ trajectoryJump, assumedFishNumber, onAssumedFishNumberChange, originalFishNumber, hasFeedData, methodResolution, ssot, overriddenOutput = null }) => {
  if (!trajectoryJump) return null;
  const fd = overriddenOutput?.finalDecision || {};
  const detected = trajectoryJump.jump_event_detected || trajectoryJump.trajectory_jump_detected;
  const confirmed = detected && (trajectoryJump.jump_event_confirmed || trajectoryJump.trajectory_jump_confirmed);
  const pending = detected && trajectoryJump.jump_confirmation_pending;
  const bestFit = trajectoryJump.best_fit_fish_number || trajectoryJump.adjusted_fish_count;
  const estMissing = trajectoryJump.estimated_missing_fish || trajectoryJump.trajectory_based_missing_fish_estimate;
  const fmt = (v) => v != null ? v.toLocaleString() : '—';
  const isAboveModel = trajectoryJump.jump_above_model !== false;
  const activeMethod = ssot?.primaryMethod || methodResolution?.activeMethod || 'fcr';
  
  // ★ ALL interpretive fields from finalDecision — no local derivation
  const detectionStrength = fd.detectionStrengthLabel || 'Low';
  
  const isFcrPrimary = activeMethod === 'fcr';
  const isJumpDisabled = isFcrPrimary && !isAboveModel;

  const strengthColor = detectionStrength === 'High' ? 'text-rose-700 bg-rose-100' : detectionStrength === 'Moderate' ? 'text-amber-700 bg-amber-100' : 'text-slate-600 bg-slate-100';

  // When FCR is primary detection mode, show a simplified disabled state
  if (isFcrPrimary) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 shadow-sm opacity-80" data-testid="sample-jump-panel">
        <div className="flex items-center gap-2 mb-2">
          <Zap className="w-5 h-5 text-gray-400" />
          <h3 className="text-sm font-semibold text-gray-500">Sample Jump Analysis</h3>
          <span className="text-xs font-medium px-2 py-0.5 rounded-full text-gray-500 bg-gray-200" data-testid="jump-panel-not-primary">
            {isJumpDisabled ? 'Not Active' : 'Secondary — FCR is primary'}
          </span>
        </div>
        {/* ★ CONSISTENCY FIX: When Not Active, show clear explanation without contradictory detection data */}
        <p className="text-xs text-gray-500 leading-relaxed" data-testid="jump-panel-fcr-primary-note">
          {isJumpDisabled 
            ? 'Jump analysis not applicable — growth is tracking at or below model expectations.'
            : (detected 
              ? 'Jump pattern detected but FCR analysis is primary. See FCR Analysis for active estimates.'
              : 'No significant weight jumps detected in sample data.')}
        </p>
      </div>
    );
  }

  return (
    <div className={`rounded-lg border ${isJumpDisabled ? 'border-gray-200 bg-gray-50 opacity-75' : 'border-slate-200 bg-white'} p-4 shadow-sm`} data-testid="sample-jump-panel">
      <div className="flex items-center gap-2 mb-3">
        <Zap className={`w-5 h-5 ${isJumpDisabled ? 'text-gray-400' : 'text-amber-500'}`} />
        <h3 className={`text-sm font-semibold ${isJumpDisabled ? 'text-gray-500' : 'text-slate-800'}`}>Sample Jump Analysis</h3>
        {isJumpDisabled && (
          <span className="text-xs font-medium px-2 py-0.5 rounded-full text-gray-500 bg-gray-200" data-testid="jump-panel-not-primary">
            Not Active
          </span>
        )}
        {!isJumpDisabled && detected && (
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${strengthColor}`} data-testid="jump-panel-confidence">
            {detectionStrength}
          </span>
        )}
        {!isJumpDisabled && !detected && fd.jumpStatus && (
          <span className="text-xs font-medium px-2 py-0.5 rounded-full text-green-700 bg-green-100" data-testid="jump-panel-no-jump">
            {fd.jumpStatus}
          </span>
        )}
      </div>

      {/* Jump Metrics — only when detected */}
      {detected && !isJumpDisabled && (() => {
        // Override-active = the user has Guardrails OFF on this report.
        // We surface a single prominent disclosure banner at the top of the
        // panel rather than repeating short caveats next to every cell.
        const overrideActive = fd.confidenceModeLabel === 'Full Data (Override)';
        return overrideActive ? (
          <div className="mb-3" data-testid="sample-jump-guardrails-off-banner">
            <GuardrailsOffDisclosure variant="short" testId="sample-jump-guardrails-off-disclosure" />
          </div>
        ) : null;
      })()}
      {detected && !isJumpDisabled && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
          <div className="space-y-0.5">
            <p className="text-xs text-gray-500 uppercase tracking-wider">Original Fish Number</p>
            <p className="text-sm font-semibold text-slate-800" data-testid="jump-original-fish">{fmt(originalFishNumber)}</p>
          </div>
          {/* Best-Fit Fish Number — mode-gated. Mirror the override rule so
              guardrails-off mode also surfaces the unconfirmed best-fit. */}
          {(() => {
            const overrideActive = fd.confidenceModeLabel === 'Full Data (Override)';
            const showStandard = confirmed && bestFit && fd.confidenceMode !== 'signal';
            const showOverride = overrideActive && bestFit;
            if (!showStandard && !showOverride) return null;
            const isUnconfirmed = !confirmed && overrideActive;
            return (
              <div className="space-y-0.5">
                <p className="text-xs text-gray-500 uppercase tracking-wider flex items-center gap-1">
                  {fd.confidenceMode === 'evidence' ? 'Best-Fit Fish (Indicative)' : 'Best-Fit Fish Number'}
                  {isUnconfirmed && (
                    <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300">
                      Unconfirmed
                    </span>
                  )}
                </p>
                <p className="text-sm font-bold text-cyan-700" data-testid="jump-bestfit-fish">
                  {(fd.confidenceMode === 'evidence' || isUnconfirmed) ? '~' : ''}{fmt(bestFit)}
                </p>
              </div>
            );
          })()}
          {/* Estimated Missing — mode-gated.
              Standard rule (guardrails ON): require confirmed jump + non-signal mode
              before exposing the estimate.
              Override rule (guardrails OFF — fd.confidenceModeLabel === 'Full Data
              (Override)'): always show the number even when the jump is unconfirmed
              or the underlying confidence mode was 'signal' (the user has explicitly
              opted into seeing all underlying signals). */}
          {(() => {
            const overrideActive = fd.confidenceModeLabel === 'Full Data (Override)';
            const baseEligible = estMissing != null && (fd.hasMissingFish || bestFit);
            const showStandard = confirmed && baseEligible && fd.confidenceMode !== 'signal';
            const showOverride = overrideActive && estMissing != null;
            if (!showStandard && !showOverride) return null;
            const isUnconfirmed = !confirmed && overrideActive;
            return (
              <div className="space-y-0.5">
                <p className="text-xs text-gray-500 uppercase tracking-wider flex items-center gap-1">
                  {fd.modeLabels?.estimatedMissingFishLabel || 'Estimated Missing Fish'}
                  {isUnconfirmed && (
                    <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300">
                      Unconfirmed
                    </span>
                  )}
                </p>
                <p className="text-sm font-bold text-rose-700" data-testid="jump-est-missing">
                  {(fd.confidenceMode === 'evidence' || isUnconfirmed) ? '~' : ''}{fmt(estMissing)}
                </p>
                {isUnconfirmed && (
                  <p className="text-[10px] text-amber-800 leading-snug font-medium">
                    Non-decision-grade — visible only because Guardrails are OFF.
                  </p>
                )}
              </div>
            );
          })()}
          <div className="space-y-0.5">
            <p className="text-xs text-gray-500 uppercase tracking-wider">Probable Loss Window</p>
            <p className="text-xs font-medium text-slate-700" data-testid="jump-loss-window">
              {trajectoryJump.probable_loss_window_start || '—'} <ArrowRight className="w-3 h-3 inline" /> {trajectoryJump.probable_loss_window_end || '—'}
            </p>
          </div>
          <div className="space-y-0.5">
            <p className="text-xs text-gray-500 uppercase tracking-wider">Jump Ratio</p>
            <p className="text-sm font-semibold text-slate-700">{trajectoryJump.jump_ratio ? `${(trajectoryJump.jump_ratio * 100).toFixed(1)}%` : '—'}</p>
          </div>
          {trajectoryJump.fit_error_score != null && (
            <div className="space-y-0.5">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Fit Error</p>
              <p className="text-sm font-semibold text-slate-700">{trajectoryJump.fit_error_score}g</p>
            </div>
          )}
          {trajectoryJump.estimation_quality && trajectoryJump.estimation_quality !== 'good' && (
            <div className="space-y-0.5">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Estimate Quality</p>
              <p className={`text-sm font-semibold ${trajectoryJump.estimation_quality === 'poor' ? 'text-red-600' : 'text-amber-600'}`} data-testid="jump-estimation-quality">
                {trajectoryJump.estimation_quality === 'poor' ? 'Low' : 'Moderate'}
                {trajectoryJump.gap_days ? ` (${trajectoryJump.gap_days}d gap)` : ''}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Assumed Fish Number Control — shown when Jump is primary and originalFishNumber is available */}
      {!isJumpDisabled && originalFishNumber && onAssumedFishNumberChange && (
        <div className="bg-slate-50 rounded-lg p-3 border border-slate-200" data-testid="assumed-fish-control">
          <div className="flex items-end gap-3 flex-wrap">
            <div className="flex-1 min-w-[160px]">
              <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Assumed Fish Number</label>
              <Input
                type="number"
                value={assumedFishNumber || ''}
                onChange={(e) => onAssumedFishNumberChange(parseInt(e.target.value) || 0)}
                className="h-9 text-sm font-mono bg-white"
                min={1}
                max={originalFishNumber * 2}
                data-testid="assumed-fish-input"
              />
            </div>
            {confirmed && bestFit && assumedFishNumber !== bestFit && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onAssumedFishNumberChange(bestFit)}
                className="h-9 text-xs border-cyan-400 text-cyan-700 hover:bg-cyan-50"
                data-testid="apply-suggested-btn"
              >
                <Target className="w-3.5 h-3.5 mr-1" />
                Apply Suggested ({fmt(bestFit)})
              </Button>
            )}
            {assumedFishNumber !== originalFishNumber && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onAssumedFishNumberChange(originalFishNumber)}
                className="h-9 text-xs text-gray-500"
                data-testid="reset-fish-btn"
              >
                Reset
              </Button>
            )}
          </div>
          {hasFeedData && assumedFishNumber !== originalFishNumber && (
            <p className="text-xs text-cyan-600 mt-1.5">
              Actual growth curve adjusted to show feed-implied weight for {fmt(assumedFishNumber)} fish.
            </p>
          )}
          {!hasFeedData && !detected && (
            <p className="text-xs text-gray-400 mt-1.5">
              Adjust the fish number to see the impact on Biological FCR and feed intensity.
            </p>
          )}
          {!hasFeedData && detected && (
            <p className="text-xs text-gray-400 mt-1.5">
              Feed data required for live curve adjustment (available in Spreadsheet Analysis).
            </p>
          )}
        </div>
      )}

      {/* Notes */}
      {!isJumpDisabled && pending && !confirmed && (
        <p className="text-xs text-yellow-700 mt-2 bg-yellow-50 rounded p-2 border border-yellow-200" data-testid="jump-pending-note">
          {fd.jumpStatus || null}
        </p>
      )}
      {!isJumpDisabled && confirmed && fd.jumpStatus && (
        <p className="text-xs text-slate-600 mt-2 leading-relaxed" data-testid="jump-confirmed-note">
          {fd.jumpStatus}
        </p>
      )}
      {/* Suspect Samples — sampling error flags */}
      {trajectoryJump.suspect_samples && trajectoryJump.suspect_samples.length > 0 && (
        <div className="mt-3 space-y-2" data-testid="suspect-samples-section">
          {trajectoryJump.suspect_samples.map((ss, idx) => (
            <div key={idx} className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-300 rounded-md">
              <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-semibold text-amber-800" data-testid={`suspect-flag-${idx}`}>
                  {ss.flag}
                </p>
                <p className="text-[10px] text-amber-700 mt-0.5">
                  Sample on {ss.sampleDate}: {ss.sampleWeight}g (expected ~{ss.expectedWeight}g from prior trajectory)
                  {ss.reversionWeight != null && ` — next sample reverted to ${ss.reversionWeight}g`}
                </p>
                <p className="text-[10px] text-amber-600 mt-0.5">{ss.reason}</p>
              </div>
            </div>
          ))}
        </div>
      )}
      {/* No-fish-loss note when all jumps reverted */}
      {!isJumpDisabled && !detected && !confirmed && !pending
        && trajectoryJump.suspect_samples?.length > 0 && trajectoryJump.jump_event_note && (
        <p className="text-xs text-amber-700 mt-2 leading-relaxed" data-testid="all-jumps-reverted-note">
          {trajectoryJump.jump_event_note}
        </p>
      )}
      {!isJumpDisabled && !detected && (
        <p className="text-xs text-slate-500 mt-2" data-testid="no-jump-note">
          No significant sample weight jumps were found. You can still manually adjust the fish number above to explore its impact on Biological FCR and feed intensity.
        </p>
      )}
    </div>
  );
};


/**
 * FCR Analysis Panel — detailed view when FCR is the primary detection method
 * Shows key metrics, confidence indicator, and SSOT-aligned missing fish evidence badge
 */
const FCRAnalysisPanel = ({ fcrBasedDetection, fcrPanelMissingFish, originalFishNumber, methodResolution, ssot, overriddenOutput = null }) => {
  const fd = overriddenOutput?.finalDecision || {};
  const activeMethod = ssot?.primaryMethod || methodResolution?.activeMethod || 'fcr';
  const isFcrPrimary = activeMethod === 'fcr';
  const fmt = (v) => v != null ? v.toLocaleString() : '—';

  // Merge data sources for RAW METRICS (numbers only — no interpretation)
  const panelData = fcrPanelMissingFish?.detected ? fcrPanelMissingFish : null;
  const engineData = fcrBasedDetection?.detected ? fcrBasedDetection : null;
  const hasData = !!(panelData || engineData);

  // RAW METRICS (numbers for tables/charts)
  const reportedFCR = fcrBasedDetection?.reportedBiologicalFCR || null;
  const modelFCR = fcrBasedDetection?.modelBiologicalFCR || null;
  const fcrGapPct = panelData?.fcrGapPercent ?? engineData?.fcrGapPercent ?? null;
  const adjustedFish = panelData?.adjustedN ?? engineData?.adjustedFishCount ?? null;
  const estMissing = panelData?.estimatedMissing ?? engineData?.estimatedMissingFish ?? null;
  const adjustedFCR = engineData?.adjustedBiologicalFCR ?? null;

  // ★ ALL interpretive fields from finalDecision — no local derivation
  const detectionStrength = fd.detectionStrengthLabel || 'Low';
  const hasEvidence = hasData && (detectionStrength === 'High' || detectionStrength === 'Moderate');

  // Severity colors (layout only — no interpretation)
  const strengthColor = detectionStrength === 'High' ? 'text-rose-700 bg-rose-100' : detectionStrength === 'Moderate' ? 'text-amber-700 bg-amber-100' : 'text-slate-600 bg-slate-100';
  const evidenceColor = fd.flags?.hasConflict ? 'bg-amber-50 border-amber-300' : detectionStrength === 'High' ? 'text-rose-700 bg-rose-50 border-rose-200' : 'text-amber-700 bg-amber-50 border-amber-200';
  const evidenceLabelColor = fd.flags?.hasConflict ? 'text-amber-700' : detectionStrength === 'High' ? 'text-rose-700' : 'text-amber-700';
  const evidenceIconColor = fd.flags?.hasConflict ? 'text-amber-600' : detectionStrength === 'High' ? 'text-rose-600' : 'text-amber-600';

  return (
    <div className={`rounded-lg border ${isFcrPrimary ? 'border-blue-200 bg-white' : 'border-gray-200 bg-gray-50 opacity-75'} p-4 shadow-sm`} data-testid="fcr-analysis-panel">
      <div className="flex items-center gap-2 mb-3">
        <TrendingDown className={`w-5 h-5 ${isFcrPrimary ? 'text-blue-600' : 'text-gray-400'}`} />
        <h3 className={`text-sm font-semibold ${isFcrPrimary ? 'text-slate-800' : 'text-gray-500'}`}>FCR Analysis</h3>
        {isFcrPrimary && hasData && (
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${strengthColor}`} data-testid="fcr-panel-confidence">
            {detectionStrength}
          </span>
        )}
        {!isFcrPrimary && (
          <span className="text-xs font-medium px-2 py-0.5 rounded-full text-gray-500 bg-gray-200" data-testid="fcr-panel-not-primary">
            Secondary — Jump Analysis is primary
          </span>
        )}
        {isFcrPrimary && !hasData && fd.fcrEvidenceLabel && (
          <span className="text-xs font-medium px-2 py-0.5 rounded-full text-green-700 bg-green-100" data-testid="fcr-panel-no-evidence">
            {fd.fcrEvidenceLabel}
          </span>
        )}
      </div>

      {/* ★ Evidence Indicator — label-only, no rephrased conclusion */}
      {isFcrPrimary && hasEvidence && fd.fcrEvidenceLabel && (
        <div className={`mb-3 p-2.5 rounded-lg border ${evidenceColor}`} data-testid="fcr-evidence-indicator">
          <div className="flex items-center gap-2">
            <AlertTriangle className={`w-4 h-4 flex-shrink-0 ${evidenceIconColor}`} />
            <span className={`text-xs font-semibold ${evidenceLabelColor}`} data-testid="fcr-evidence-label">
              {fd.fcrEvidenceLabel}
            </span>
          </div>
        </div>
      )}

      {/* When the global Guardrails toggle is OFF, all numeric estimates in
          this panel are surfaced regardless of confidence. Add a single
          prominent disclosure so the reader knows the figures wouldn't be
          shown otherwise. */}
      {fd.confidenceModeLabel === 'Full Data (Override)' && hasData && (
        <div className="mb-3" data-testid="fcr-guardrails-off-banner">
          <GuardrailsOffDisclosure variant="short" testId="fcr-guardrails-off-disclosure" />
        </div>
      )}

      {hasData && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
          <div className="space-y-0.5">
            <p className="text-xs text-gray-500 uppercase tracking-wider">Reported FCR</p>
            <p className="text-sm font-semibold text-slate-800" data-testid="fcr-panel-reported">{reportedFCR ?? '—'}</p>
          </div>
          <div className="space-y-0.5">
            <p className="text-xs text-gray-500 uppercase tracking-wider">Model FCR</p>
            <p className="text-sm font-semibold text-slate-800" data-testid="fcr-panel-model">{modelFCR ?? '—'}</p>
          </div>
          <div className="space-y-0.5">
            <p className="text-xs text-gray-500 uppercase tracking-wider">FCR Gap</p>
            <p className="text-sm font-bold text-blue-700" data-testid="fcr-panel-gap">{fcrGapPct != null ? `${Math.abs(fcrGapPct).toFixed(1)}%` : '—'}</p>
          </div>
          {originalFishNumber && (
            <div className="space-y-0.5">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Original Fish Count</p>
              <p className="text-sm font-semibold text-slate-800" data-testid="fcr-panel-original-fish">{fmt(originalFishNumber)}</p>
            </div>
          )}
          {adjustedFish != null && fd.hasMissingFish && fd.confidenceMode !== 'signal' && (
            <div className="space-y-0.5">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Adjusted Fish Count</p>
              <p className="text-sm font-bold text-blue-700" data-testid="fcr-panel-adjusted-fish">
                {fd.confidenceMode === 'evidence' ? '~' : ''}{fmt(adjustedFish)}
              </p>
            </div>
          )}
          {estMissing != null && fd.hasMissingFish && fd.confidenceMode !== 'signal' && (
            <div className="space-y-0.5">
              <p className="text-xs text-gray-500 uppercase tracking-wider">
                {fd.confidenceMode === 'evidence' ? 'Est. Missing (Indicative)' : 'Estimated Missing'}
              </p>
              <p className="text-sm font-bold text-rose-700" data-testid="fcr-panel-est-missing">
                {fd.confidenceMode === 'evidence' ? '~' : ''}{fmt(estMissing)}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Additional metrics row when FCR is primary — hide precise estimates in signal mode */}
      {isFcrPrimary && hasData && adjustedFCR && fd.confidenceMode !== 'signal' && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3 pt-2 border-t border-slate-100">
          <div className="space-y-0.5">
            <p className="text-xs text-gray-500 uppercase tracking-wider">Adjusted FCR</p>
            <p className="text-sm font-semibold text-blue-700" data-testid="fcr-panel-adjusted-fcr">{adjustedFCR}</p>
          </div>
          {estMissing != null && originalFishNumber && fd.hasMissingFish && (
            <div className="space-y-0.5">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Missing %</p>
              <p className="text-sm font-semibold text-rose-600" data-testid="fcr-panel-missing-pct">
                {fd.confidenceMode === 'evidence' ? '~' : ''}{((estMissing / originalFishNumber) * 100).toFixed(1)}%
              </p>
            </div>
          )}
        </div>
      )}

      {/* FCR explanation — from finalDecision only */}
      {hasData && fd.fcrEvidenceLabel && (
        <p className="text-xs text-slate-600 leading-relaxed" data-testid="fcr-panel-explanation">
          {fd.fcrEvidenceLabel}
        </p>
      )}
      {!hasData && (
        <p className="text-xs text-slate-500" data-testid="fcr-panel-no-data">
          {fd.fcrEvidenceLabel || null}
        </p>
      )}
    </div>
  );
};


/**
 * Confirmed Trajectory Jump Alert
 */
const TrajectoryJumpAlert = ({ trajectoryJump, methodResolution, ssot, overriddenOutput = null }) => {
  const fd = overriddenOutput?.finalDecision || {};
  if (!trajectoryJump) return null;
  
  const detected = trajectoryJump.jump_event_detected || trajectoryJump.trajectory_jump_detected;
  if (!detected) return null;
  
  const activeMethod = ssot?.primaryMethod || methodResolution?.activeMethod || 'fcr';
  const confirmed = trajectoryJump.jump_event_confirmed || trajectoryJump.trajectory_jump_confirmed;
  const pending = trajectoryJump.jump_confirmation_pending;
  // ★ SSOT: Use detection strength from root
  const detectionStrength = ssot?.detectionStrength || 'Low';
  const isAboveModel = trajectoryJump.jump_above_model !== false;
  const fmt = (v) => v != null ? v.toLocaleString() : '—';

  // When FCR is the active method and jump is below model, show grayed-out "Not used" state
  // But when Jump IS the active method, always show the full alert
  if (activeMethod === 'fcr' && !isAboveModel) {
    return (
      <div className="rounded-lg border p-4 bg-gray-50 border-gray-200 opacity-70" data-testid="trajectory-jump-alert">
        <div className="flex items-start gap-3">
          <Zap className="w-5 h-5 mt-0.5 flex-shrink-0 text-gray-400" />
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="text-sm font-semibold text-gray-500">Sample Jump Analysis — Not Used</h4>
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-200 text-gray-500" data-testid="trajectory-jump-confidence">
                Not Primary
              </span>
            </div>
            <p className="text-xs text-gray-400 leading-relaxed" data-testid="trajectory-jump-note">
              {fd.jumpStatus || null}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const isHigh = detectionStrength === 'High';
  const isMod = detectionStrength === 'Moderate';

  // Pending confirmation — yellow warning
  if (pending && !confirmed) {
    return (
      <div className="rounded-lg border p-4 bg-yellow-50 border-yellow-300" data-testid="trajectory-jump-alert">
        <div className="flex items-start gap-3">
          <Zap className="w-5 h-5 mt-0.5 flex-shrink-0 text-yellow-600" />
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="text-sm font-semibold text-yellow-800">Sample Jump — Pending</h4>
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-yellow-200 text-yellow-800">Pending</span>
            </div>
            <p className="text-xs text-yellow-700 leading-relaxed mb-2" data-testid="trajectory-jump-note">
              {trajectoryJump.jump_event_note || trajectoryJump.note || null}
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-center">
              <div className="bg-white rounded p-2 border border-gray-200">
                <p className="text-xs text-gray-500 uppercase">Jump Sample</p>
                <p className="text-xs font-semibold text-gray-800">{trajectoryJump.jump_sample_date || '—'}</p>
              </div>
              <div className="bg-white rounded p-2 border border-gray-200">
                <p className="text-xs text-gray-500 uppercase">Jump Ratio</p>
                <p className="text-xs font-semibold text-gray-800">{trajectoryJump.jump_ratio ? `${(trajectoryJump.jump_ratio * 100).toFixed(1)}%` : '—'}</p>
              </div>
              <div className="bg-white rounded p-2 border border-gray-200">
                <p className="text-xs text-gray-500 uppercase">Status</p>
                <p className="text-xs font-semibold text-yellow-700">Awaiting Next Sample</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Confirmed jump — rose (high) or amber (moderate/low) card
  const bgClass = isHigh ? 'bg-rose-50 border-rose-300' : isMod ? 'bg-amber-50 border-amber-300' : 'bg-slate-50 border-slate-300';
  const textClass = isHigh ? 'text-rose-800' : isMod ? 'text-amber-800' : 'text-slate-800';
  const subClass = isHigh ? 'text-rose-700' : isMod ? 'text-amber-700' : 'text-slate-700';
  const iconClass = isHigh ? 'text-rose-600' : isMod ? 'text-amber-600' : 'text-slate-600';
  const badgeClass = isHigh ? 'bg-rose-200 text-rose-800' : isMod ? 'bg-amber-200 text-amber-800' : 'bg-slate-200 text-slate-800';

  const alertTitle = fd.jumpStatus || null;

  return (
    <div className={`rounded-lg border p-4 ${bgClass}`} data-testid="trajectory-jump-alert">
      <div className="flex items-start gap-3">
        <Zap className={`w-5 h-5 mt-0.5 flex-shrink-0 ${iconClass}`} />
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h4 className={`text-sm font-semibold ${textClass}`}>
              {alertTitle}
            </h4>
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${badgeClass}`} data-testid="trajectory-jump-confidence">
              {detectionStrength} Detection Strength
            </span>
          </div>
          <p className={`text-xs ${subClass} leading-relaxed mb-2`} data-testid="trajectory-jump-note">
            {fd.jumpStatus || null}
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-center">
            <div className="bg-white rounded p-2 border border-gray-200">
              <p className="text-xs text-gray-500 uppercase">Loss Window</p>
              <p className="text-xs font-semibold text-gray-800" data-testid="trajectory-loss-window">
                {trajectoryJump.probable_loss_window_start} — {trajectoryJump.probable_loss_window_end}
              </p>
            </div>
            <div className="bg-white rounded p-2 border border-gray-200">
              <p className="text-xs text-gray-500 uppercase">Jump Ratio</p>
              <p className="text-xs font-semibold text-gray-800">
                {trajectoryJump.jump_ratio ? `${(trajectoryJump.jump_ratio * 100).toFixed(1)}%` : fmt(null)}
              </p>
            </div>
            {/* Best-Fit Fish Count — ALWAYS SHOWN regardless of confidence mode */}
            {(trajectoryJump.best_fit_fish_number || trajectoryJump.adjusted_fish_count) && (
            <div className="bg-white rounded p-2 border border-gray-200">
              <p className="text-xs text-gray-500 uppercase">Best-Fit Fish Count</p>
              <p className="text-xs font-bold text-cyan-700" data-testid="trajectory-best-fit">
                {fmt(trajectoryJump.best_fit_fish_number || trajectoryJump.adjusted_fish_count)}
              </p>
            </div>
            )}
            {/* Mode-gated: hide in signal mode */}
            {fd.hasMissingFish && fd.confidenceMode !== 'signal' && (
            <div className="bg-white rounded p-2 border border-gray-200">
              <p className="text-xs text-gray-500 uppercase">
                {fd.modeLabels?.estimatedMissingFishLabel ? fd.modeLabels.estimatedMissingFishLabel.substring(0, 15) + '.' : 'Est. Missing Fish'}
              </p>
              <p className="text-xs font-bold text-rose-700" data-testid="trajectory-missing-fish">
                {fd.confidenceMode === 'evidence' ? '~' : ''}{fmt(trajectoryJump.estimated_missing_fish || trajectoryJump.trajectory_based_missing_fish_estimate)}
              </p>
            </div>
            )}
          </div>

          {/* Extended details row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2 text-center">
            <div className="bg-white rounded p-2 border border-gray-200">
              <p className="text-xs text-gray-500 uppercase">Expected Weight</p>
              <p className="text-xs font-semibold text-gray-800">
                {trajectoryJump.expected_weight_at_jump_from_prior_trajectory || '—'}g
              </p>
            </div>
            <div className="bg-white rounded p-2 border border-gray-200">
              <p className="text-xs text-gray-500 uppercase">Confirmation Date</p>
              <p className="text-xs font-semibold text-gray-800">
                {trajectoryJump.confirmation_sample_date || trajectoryJump.confirmDate || '—'}
              </p>
            </div>
            <div className="bg-white rounded p-2 border border-gray-200">
              <p className="text-xs text-gray-500 uppercase">Fit Error</p>
              <p className="text-xs font-semibold text-gray-800">
                {trajectoryJump.fit_error_score != null ? `${trajectoryJump.fit_error_score}g` : '—'}
              </p>
            </div>
            <div className="bg-white rounded p-2 border border-gray-200">
              <p className="text-xs text-gray-500 uppercase">Jump Sample</p>
              <p className="text-xs font-semibold text-gray-800">{trajectoryJump.jump_sample_date || '—'}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Feeding Impact Classification — displays RFI-based feeding state
 * and contextual interpretation when missing fish are detected.
 * Stock accuracy is PRIMARY; feeding impact is SECONDARY.
 */
const FeedingImpactIndicator = ({ ssot, overriddenFeedingNote = null }) => {
  // ★ Use overriddenFeedingNote first (SSOT-corrected), then feedingNote
  if (!ssot?.feedingImpact && !ssot?.feedingNote && !overriddenFeedingNote) return null;

  const { rfi, feedingState, feedingImpact, feedingNote } = ssot;
  const displayNote = overriddenFeedingNote || feedingNote || feedingImpact;
  const isOverfeeding = feedingState === 'Overfeeding';
  const isRestricted = feedingState === 'Restricted';
  const isMixed = feedingState === 'Mixed';  // ★ Handle corrected "Mixed" state

  const bg = isOverfeeding ? 'bg-orange-50 border-orange-300' 
           : isRestricted ? 'bg-blue-50 border-blue-200' 
           : isMixed ? 'bg-amber-50 border-amber-300'
           : 'bg-amber-50 border-amber-200';
  const iconColor = isOverfeeding ? 'text-orange-600' 
                  : isRestricted ? 'text-blue-600' 
                  : isMixed ? 'text-amber-600'
                  : 'text-amber-600';
  const titleColor = isOverfeeding ? 'text-orange-800' 
                   : isRestricted ? 'text-blue-800' 
                   : isMixed ? 'text-amber-800'
                   : 'text-amber-800';
  const noteColor = isOverfeeding ? 'text-orange-700' 
                  : isRestricted ? 'text-blue-700' 
                  : isMixed ? 'text-amber-700'
                  : 'text-amber-700';
  const badgeBg = isOverfeeding ? 'bg-orange-200 text-orange-800' 
                : isRestricted ? 'bg-blue-200 text-blue-800' 
                : isMixed ? 'bg-amber-200 text-amber-800'
                : 'bg-amber-200 text-amber-800';

  return (
    <div className={`rounded-lg border p-4 ${bg}`} data-testid="feeding-impact-indicator">
      <div className="flex items-start gap-3">
        <Flame className={`w-5 h-5 mt-0.5 flex-shrink-0 ${iconColor}`} />
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h4 className={`text-sm font-semibold ${titleColor}`}>Feeding Impact Classification</h4>
            <VisibilityGate sectionKey="feeding.state" label="Feeding State">
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${badgeBg}`} data-testid="feeding-impact-state">
                {feedingState}
              </span>
            </VisibilityGate>
            <VisibilityGate sectionKey="feeding.rfi" label="RFI Value">
            {rfi != null && (
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-700" data-testid="feeding-impact-rfi">
                RFI: {rfi}%
              </span>
            )}
            </VisibilityGate>
          </div>
          <VisibilityGate sectionKey="feeding.note" label="Feeding Note">
            <p className={`text-xs ${noteColor} leading-relaxed`} data-testid="feeding-impact-message">
              {displayNote}
            </p>
          </VisibilityGate>
          <VisibilityGate sectionKey="feeding.recent_alert" label="Recent RFI Alert">
          {ssot.recentRfi != null && ssot.recentFeedingState && ssot.recentFeedingState !== feedingState && (
            <p className={`text-xs mt-1 font-medium ${
              ssot.recentFeedingState === 'Overfeeding' ? 'text-red-600' : 
              ssot.recentFeedingState === 'Optimal' ? 'text-green-600' : 
              ssot.recentFeedingState === 'Mixed' ? 'text-amber-600' :
              'text-blue-600'
            }`} data-testid="feeding-impact-recent-alert">
              Recent 14-day RFI: {ssot.recentRfi}% ({ssot.recentFeedingState}) — {ssot.recentFeedingState === 'Overfeeding' ? 'recent overfeeding vs cumulative underfeeding' : 'divergent from cumulative pattern'}
            </p>
          )}
          </VisibilityGate>
        </div>
      </div>
    </div>
  );
};

/**
 * Cannibalism Risk Indicator — Track days with high-risk conditions
 * Displays when RFI < 50%, temperature > 21°C, and fish weight < 100g
 */
const CannibalismRiskIndicator = ({ ssot }) => {
  if (!ssot?.cannibalismRisk?.detected) return null;
  
  const { cannibalismRisk } = ssot;
  const { daysAtRisk, riskLevel, message, explanation, recommendation, periods } = cannibalismRisk;
  
  const bgColor = riskLevel === 'High' ? 'bg-red-50 border-red-300' 
                : riskLevel === 'Moderate' ? 'bg-orange-50 border-orange-300'
                : 'bg-amber-50 border-amber-300';
  const iconColor = riskLevel === 'High' ? 'text-red-600' 
                  : riskLevel === 'Moderate' ? 'text-orange-600'
                  : 'text-amber-600';
  const titleColor = riskLevel === 'High' ? 'text-red-800' 
                   : riskLevel === 'Moderate' ? 'text-orange-800'
                   : 'text-amber-800';
  const badgeBg = riskLevel === 'High' ? 'bg-red-200 text-red-800' 
                : riskLevel === 'Moderate' ? 'bg-orange-200 text-orange-800'
                : 'bg-amber-200 text-amber-800';
  
  return (
    <VisibilityGate sectionKey="mfi.cannibalism_risk" label="Cannibalism Risk Indicator">
      <div className={`rounded-lg border p-4 ${bgColor}`} data-testid="cannibalism-risk-indicator">
        <div className="flex items-start gap-3">
          <AlertTriangle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${iconColor}`} />
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h4 className={`text-sm font-semibold ${titleColor}`}>Cannibalism Risk Indicator</h4>
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${badgeBg}`}>
                {riskLevel} Risk · {daysAtRisk} day{daysAtRisk !== 1 ? 's' : ''}
              </span>
            </div>
            <p className={`text-sm ${titleColor.replace('800', '700')} mt-1`}>{message}</p>
            {explanation && (
              <p className="text-xs text-gray-600 mt-2 leading-relaxed">{explanation}</p>
            )}
            {recommendation && (
              <p className={`text-xs font-medium mt-2 ${titleColor.replace('800', '700')}`}>
                Recommendation: {recommendation}
              </p>
            )}
            {periods && periods.length > 0 && (
              <div className="mt-3 pt-2 border-t border-gray-200">
                <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Risk Periods (sample)</p>
                <div className="flex flex-wrap gap-1">
                  {periods.slice(0, 5).map((p, idx) => (
                    <span key={idx} className="text-[10px] bg-white/60 text-gray-600 px-1.5 py-0.5 rounded">
                      {p.date}: RFI {p.rfi}%, {p.temperature}°C, {p.weight}g
                    </span>
                  ))}
                  {periods.length > 5 && (
                    <span className="text-[10px] text-gray-500">+{periods.length - 5} more</span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </VisibilityGate>
  );
};

/**
 * Method Conflict Warning — Alert when FCR and Jump show different estimates
 * Helps users understand why the same cage might show different numbers
 */
const MethodConflictWarning = ({ ssot }) => {
  if (!ssot?.methodConflictComparison?.hasConflict) return null;
  
  const { methodConflictComparison } = ssot;
  const { 
    fcrEstimate, jumpEstimate, percentDifference, primaryMethod,
    conflictExplanation, reliabilityNote, fcrHasFeedData, jumpHasConfirmedSignal 
  } = methodConflictComparison;
  
  return (
    <VisibilityGate sectionKey="mfi.method_conflict" label="Method Conflict Warning">
      <div className="rounded-lg border-2 border-amber-400 bg-amber-50 p-4" data-testid="method-conflict-warning">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0 text-amber-600" />
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <h4 className="text-sm font-bold text-amber-800">Detection Method Discrepancy</h4>
              {percentDifference > 0 && (
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-200 text-amber-800">
                  {percentDifference}% difference
                </span>
              )}
            </div>
            
            {/* Side-by-side comparison */}
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div className={`rounded-lg p-3 ${fcrHasFeedData ? 'bg-white border-2 border-blue-300' : 'bg-gray-100 border border-gray-200'}`}>
                <p className="text-[10px] text-gray-500 uppercase tracking-wider">FCR Analysis</p>
                <p className={`text-lg font-bold ${fcrHasFeedData ? 'text-blue-700' : 'text-gray-400'}`}>
                  {fcrEstimate > 0 ? fcrEstimate.toLocaleString() : 'N/A'}
                </p>
                <p className="text-[10px] text-gray-500">
                  {fcrHasFeedData ? '✓ Has feed data' : '✗ Missing feed data'}
                </p>
                {primaryMethod === 'fcr' && fcrEstimate > 0 && (
                  <span className="inline-block mt-1 text-[9px] font-semibold px-1.5 py-0.5 rounded bg-blue-600 text-white">RECOMMENDED</span>
                )}
              </div>
              <div className={`rounded-lg p-3 ${jumpHasConfirmedSignal ? 'bg-white border-2 border-purple-300' : 'bg-gray-100 border border-gray-200'}`}>
                <p className="text-[10px] text-gray-500 uppercase tracking-wider">Jump Analysis</p>
                <p className={`text-lg font-bold ${jumpHasConfirmedSignal ? 'text-purple-700' : 'text-gray-400'}`}>
                  {jumpEstimate > 0 ? jumpEstimate.toLocaleString() : 'N/A'}
                </p>
                <p className="text-[10px] text-gray-500">
                  {jumpHasConfirmedSignal ? '✓ Signal confirmed' : '✗ No confirmed signal'}
                </p>
                {primaryMethod === 'jump' && jumpEstimate > 0 && (
                  <span className="inline-block mt-1 text-[9px] font-semibold px-1.5 py-0.5 rounded bg-purple-600 text-white">RECOMMENDED</span>
                )}
              </div>
            </div>
            
            {conflictExplanation && (
              <p className="text-xs text-amber-800 leading-relaxed mb-2">{conflictExplanation}</p>
            )}
            {reliabilityNote && (
              <p className="text-xs text-gray-600 italic">{reliabilityNote}</p>
            )}
          </div>
        </div>
      </div>
    </VisibilityGate>
  );
};

/**
 * Biomass Uncertainty Range — ±5% tolerance display
 */

/**
 * Sampling Recommendation — ★ SSOT ONLY: reads from overriddenOutput
 */
const SamplingRecommendation = ({ finalDecision, dataQuality = null }) => {
  const fd = finalDecision || {};
  if (!fd.samplingRecommendation) return null;

  const isUrgent = fd.samplingPriority === 'urgent';

  // PRD UX-fix #6: surface direct operational sampling guidance whenever the
  // sample frequency score is low/moderate OR the average gap exceeds 30
  // days. Triggers from data-quality only — no calculation/threshold change.
  const sampleFrequencyScore = dataQuality?.subscores?.sampleFrequency;
  const avgIntervalRaw = dataQuality?.details?.avgSampleInterval;
  const avgIntervalDays = typeof avgIntervalRaw === 'number'
    ? avgIntervalRaw
    : (typeof avgIntervalRaw === 'string' ? parseFloat(avgIntervalRaw) : null);
  const showOperationalGuidance = !!(
    (avgIntervalDays != null && !Number.isNaN(avgIntervalDays) && avgIntervalDays > 30) ||
    (typeof sampleFrequencyScore === 'number' && sampleFrequencyScore < 60)
  );

  return (
    <div
      className={`rounded-lg border p-4 flex items-start gap-3 ${
        isUrgent
          ? 'bg-red-50 border-red-300'
          : 'bg-sky-50 border-sky-300'
      }`}
      data-testid="sampling-recommendation"
    >
      <Crosshair className={`w-5 h-5 mt-0.5 flex-shrink-0 ${isUrgent ? 'text-red-600' : 'text-sky-600'}`} />
      <div>
        <h4 className={`text-sm font-semibold ${isUrgent ? 'text-red-800' : 'text-sky-800'}`}>
          Sampling Recommendation
        </h4>
        <VisibilityGate sectionKey="sampling.recommendation" label="Recommendation Text">
          <p
            className={`text-sm font-medium mt-1 ${isUrgent ? 'text-red-700' : 'text-sky-700'}`}
            data-testid="sampling-recommendation-text"
          >
            {fd.samplingRecommendation}
          </p>
        </VisibilityGate>
        {showOperationalGuidance && (
          <div className="mt-2 text-xs text-sky-800 bg-white/60 border border-sky-200 rounded-md px-2.5 py-2 leading-relaxed" data-testid="sampling-operational-guidance">
            <p className="font-semibold mb-1">Sampling guidance</p>
            <ul className="space-y-0.5">
              <li>• Average sample interval: <span className="font-semibold">{avgIntervalDays != null && !Number.isNaN(avgIntervalDays) ? `${Math.round(avgIntervalDays)} days` : 'unavailable'}</span></li>
              <li>• Recommended operational interval: <span className="font-semibold">≤ 30 days</span></li>
              <li>• Additional samples required to increase confidence.</li>
            </ul>
          </div>
        )}
        <VisibilityGate sectionKey="sampling.confidence" label="Sampling Confidence">
        {fd.samplingConfidence && (
          <p className="text-xs text-gray-500 mt-1">
            Confidence: {fd.samplingConfidence}
          </p>
        )}
        </VisibilityGate>
      </div>
    </div>
  );
};

/**
 * Growth Intelligence Summary — top-level consolidated view of all stored analysis fields
 */
const GrowthIntelligenceSummary = ({ data, sharedMetrics, trajectoryJump, ssot, overriddenOutput = null, decisionContext = null, methodResolution = null, growthFit = null, fcrBasedDetection = null }) => {
  if (!data) return null;
  const fd = overriddenOutput?.finalDecision || {};

  const sm = sharedMetrics || {};
  
  // Derive activeMethod and primaryMethodLabel from ssot/methodResolution
  const activeMethod = ssot?.primaryMethod || methodResolution?.activeMethod || 'fcr';
  const primaryMethodLabel = ssot?.primaryMethodLabel || (activeMethod === 'jump' ? 'Jump-Based' : 'FCR-Based');
  
  // Growth Summary Notice from finalDecision
  const growthSummaryNotice = fd.growthSummaryNotice || null;
  
  // Detection strength drivers from SSOT (factual labels only)
  const ssotDrivers = ssot?.detectionStrengthDrivers || [];
  const fmtNum = (v, dec = 0) => v != null ? v.toLocaleString(undefined, { minimumFractionDigits: dec, maximumFractionDigits: dec }) : '\u2014';

  const rows = [
    fd.growthDirectionLabel && {
      label: 'Growth Direction',
      visKey: 'gis.growth_direction',
      value: `${fd.growthDirectionLabel} (${sm?.deviation_percent != null ? (sm.deviation_percent > 0 ? '+' : '') + sm.deviation_percent.toFixed(1) + '%' : '\u2014'})`,
      highlight: fd.growthDirectionState !== 'within',
      warning: fd.growthDirectionWarning,
    },
    {
      label: 'Detection Strength',
      visKey: 'gis.detection_strength',
      value: fd.detectionStrengthLabel ? `${fd.detectionStrengthLabel} (${fd.detectionStrengthScore}/100)` : '\u2014',
      highlight: fd.detectionStrengthScore >= 40,
    },
    fd.dataQualityLabel && {
      label: 'Data Quality',
      visKey: 'gis.data_quality',
      value: `${fd.dataQualityLabel} (${fd.dataQualityScore ?? 0}/100)`,
    },
    fd.trendClassificationLabel && {
      label: 'Trend Classification',
      visKey: 'gis.trend_classification',
      value: fd.trendClassificationLabel,
    },
    fd.missingFishAssessment && {
      label: fd.modeLabels?.missingFishAssessmentLabel || (fd.hasMissingFish ? 'Missing Fish Assessment' : 'Stock Assessment'),
      visKey: 'gis.deviation_assessment',
      value: fd.missingFishAssessment,
      highlight: fd.hasMissingFish && fd.confidenceMode !== 'signal',
    },
    // Adjusted Fish Count — hidden in signal mode
    fd.adjustedFishCountLabel && fd.hasMissingFish && fd.confidenceMode !== 'signal' && {
      label: 'Adjusted Fish Count',
      visKey: 'gis.adjusted_fish_count',
      value: fd.adjustedFishCountLabel,
      highlight: true,
    },
    // Missing fish count — mode-gated display
    fd.confidenceMode !== 'signal' && {
      label: fd.modeLabels?.estimatedMissingFishLabel || 'Estimated Missing Fish',
      visKey: 'gis.estimated_missing_fish',
      value: fd.missingFishCountDisplay || (fd.hasMissingFish && fd.missingFishCount ? fmtNum(fd.missingFishCount) : '\u2014'),
      highlight: fd.hasMissingFish,
      indicative: fd.confidenceMode === 'evidence',
    },
  ].filter(Boolean);

  // Feeding Impact row — from finalDecision only
  if (fd.feedingImpactLabel) {
    rows.push({
      label: 'Feeding Impact',
      visKey: 'gis.feeding_impact',
      value: `${fd.feedingImpactLabel} (RFI: ${fd.feedingImpactRfi ?? '\u2014'}%)`,
      highlight: fd.feedingImpactLabel === 'Overfeeding',
    });
  }

  // Suspect sample row — metric only + finalDecision warning
  const suspectSamples = trajectoryJump?.suspect_samples || [];
  if (suspectSamples.length > 0 && fd.suspectSampleWarning) {
    rows.push({
      label: 'Suspect Sample',
      value: suspectSamples.map(ss =>
        `${ss.sampleDate} (${ss.sampleWeight}g)`
      ).join(', '),
      highlight: true,
      warning: fd.suspectSampleWarning,
    });
  }

  return (
    <div className="bg-slate-900 text-white rounded-lg p-5 space-y-3" data-testid="growth-intelligence-summary">
      {/* ★ DECISION CONTEXT PANEL — Structured decision support at TOP */}
      {decisionContext && <DecisionContextPanel decisionContext={decisionContext} ssot={ssot} finalDecision={fd} growthFit={growthFit} analyticalSignals={data?.analyticalSignals || null} fcrBasedDetection={fcrBasedDetection} />}

      {/* ★ FCR OUTLIER CALLOUT — Diagnostic: periods excluded due to implausible FCR */}
      <FCROutlierCallout ssot={ssot} />
      
      <div className="flex items-center justify-between">
        <h4 className="text-base font-bold flex items-center gap-2 text-slate-100">
          <ClipboardList className="w-5 h-5 text-cyan-400" />
          Growth Intelligence Summary
        </h4>
        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${activeMethod === 'jump' ? 'bg-amber-500/20 text-amber-300' : 'bg-cyan-500/20 text-cyan-300'}`} data-testid="summary-method-badge">
          {primaryMethodLabel}
        </span>
      </div>

      {/* ★ Growth Summary Notice — from overriddenOutput ONLY, NO local "Growth deviation detected" */}
      {growthSummaryNotice && (
        <p className={`text-sm font-medium ${overriddenOutput?.hasConflict ? 'text-amber-400' : 'text-amber-400'}`} data-testid="growth-summary-notice">
          {growthSummaryNotice}
        </p>
      )}

      <div className="grid grid-cols-1 divide-y divide-slate-700">
        {rows.map((r, i) => (
          <VisibilityGate key={i} sectionKey={r.visKey} label={r.label}>
            <div className="flex justify-between items-start py-2 gap-4" data-testid={`summary-row-${r.label.toLowerCase().replace(/\s+/g, '-')}`}>
              <span className="text-xs text-slate-400 font-medium shrink-0">{r.label}</span>
              <span className={`text-sm text-right font-semibold ${r.highlight ? 'text-amber-400' : 'text-slate-100'}`}>
                {r.value}
                {r.warning && (
                  <span className="block text-xs text-red-400 font-normal mt-0.5">{r.warning}</span>
                )}
              </span>
            </div>
          </VisibilityGate>
        ))}
      </div>
      {/* Detection Strength Drivers (from SSOT — factual labels, not narrative) */}
      {ssotDrivers.length > 0 && (
        <div className="mt-2 pt-2 border-t border-slate-700/30">
          <p className="text-[10px] text-slate-400 mb-1">Detection strength drivers:</p>
          <div className="flex flex-wrap gap-1">
            {ssotDrivers.map((d, i) => (
              <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-700/40 text-slate-300">{d}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Data Quality Indicator — scored 0–100 with sub-score breakdown
 */
const DataQualityIndicator = ({ dataQuality }) => {
  if (!dataQuality) return null;

  const { score, warning } = dataQuality;
  const subscores = dataQuality.subscores || {};
  const details = dataQuality.details || {};

  const barColor = (v) =>
    v >= 80 ? 'bg-emerald-500' : v >= 60 ? 'bg-amber-500' : 'bg-red-500';
  const labelColor = (v) =>
    v >= 80 ? 'text-emerald-700' : v >= 60 ? 'text-amber-700' : 'text-red-700';

  const rows = [
    { label: 'Sample Frequency',     visKey: 'dq.sample_frequency',     value: subscores.sampleFrequency ?? 0,    weight: '40%', detail: details.avgSampleInterval ? `~${details.avgSampleInterval}d interval` : 'n/a' },
    { label: 'Feed Continuity',      visKey: 'dq.feed_continuity',      value: subscores.feedContinuity ?? 0,     weight: '25%', detail: `${details.feedCoveragePercent ?? 0}% coverage` },
    { label: 'Mortality Reporting',  visKey: 'dq.mortality_reporting',  value: subscores.mortalityReporting ?? 0, weight: '20%', detail: `${details.mortalityCoveragePercent ?? 0}% coverage` },
    { label: 'Temperature Data',     visKey: 'dq.temperature_data',     value: subscores.temperatureData ?? 0,    weight: '15%', detail: `${details.temperatureCoveragePercent ?? 0}% coverage` },
  ];

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3" data-testid="data-quality-indicator">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
          <Database className="w-4 h-4 text-blue-600" />
          Data Quality Indicator
        </h4>
        <VisibilityGate sectionKey="dq.overall_score" label="Overall Score">
          <div className="flex items-center gap-2">
            <span className={`text-xl font-bold ${labelColor(score)}`} data-testid="data-quality-score">{score}</span>
            <span className="text-xs text-gray-500">/ 100</span>
          </div>
        </VisibilityGate>
      </div>

      {/* Composite bar */}
      <div className="w-full bg-gray-200 rounded-full h-2.5">
        <div className={`h-2.5 rounded-full transition-all ${barColor(score)}`} style={{ width: `${score}%` }} />
      </div>

      {/* Lowest driver hint */}
      {(() => {
        const lowest = rows.reduce((min, r) => r.value < min.value ? r : min, rows[0]);
        if (lowest.value < 70) {
          return (
            <p className="text-xs text-slate-600 bg-slate-50 rounded px-2 py-1">
              <span className="font-medium">Improve score:</span> {lowest.label} is lowest ({lowest.value}/100)
            </p>
          );
        }
        return null;
      })()}

      <VisibilityGate sectionKey="dq.warning" label="Data Quality Warning">
      {warning && (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="data-quality-warning">
          <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
          <span className="text-xs text-red-700">{warning}</span>
        </div>
      )}
      </VisibilityGate>

      {/* Sub-scores */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {rows.map((r) => (
          <VisibilityGate key={r.label} sectionKey={r.visKey} label={r.label}>
            <div className="bg-gray-50 rounded-md p-2 text-center">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{r.label}</p>
              <p className={`text-lg font-bold ${labelColor(r.value)}`}>{r.value}</p>
              <p className="text-xs text-gray-400">{r.detail} · {r.weight}</p>
            </div>
          </VisibilityGate>
        ))}
      </div>
    </div>
  );
};

/**
 * Mortality Reality Check — advisory when mortality is unusually low
 */
const MortalityRealityCheck = ({ mortalityCheck }) => {
  if (!mortalityCheck || !mortalityCheck.triggered) return null;

  return (
    <div className="bg-amber-50 border border-amber-300 rounded-lg p-4" data-testid="mortality-reality-check">
      <div className="flex items-start gap-3">
        <ShieldAlert className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
        <div>
          <h4 className="text-sm font-semibold text-amber-800 mb-1">Mortality Reality Check</h4>
          <p className="text-xs text-amber-700 mb-2">
            Reported mortality: <span className="font-bold">{mortalityCheck.mortalityPercent}%</span>
          </p>
          <p className="text-xs text-amber-800 font-medium">
            Reported mortality unusually low relative to biomass trend.
          </p>
          {/* ★ REMOVED: Speculative "Possible causes" list — no local narrative */}
          {/* User-facing explanations come from overriddenOutput.finalExplanationList only */}
          <p className="text-xs text-amber-700 mt-1">
            Review mortality records for completeness.
          </p>
        </div>
      </div>
    </div>
  );
};

/**
 * Economic Impact Section - displays avoidable feed cost, revenue risk, and delayed detection cost
 * Mode-gated: hidden in signal mode, indicative in evidence mode, full in decision mode
 */
const EconomicImpactSection = ({ economicImpact, ssot, finalDecision, dynamicFeedVariance = null }) => {
  const fd = finalDecision || {};
  
  // Signal mode: hide entire section UNLESS user override is active
  if (fd.confidenceMode === 'signal' && !fd.showFinancialImpact) {
    return null;
  }
  
  // ★ DYNAMIC FEED VARIANCE: Use dynamicFeedVariance when available (updates when fish count changes)
  // Otherwise fall back to static SSOT values
  const dynamicWastedFeedKg = dynamicFeedVariance?.wastedFeedKg;
  const dynamicFeedCostImpact = dynamicFeedVariance?.feedCostImpact;
  const dynamicFeedCostPerKg = dynamicFeedVariance?.feedCostPerKg;
  
  // Use dynamic values if available, otherwise fall back to SSOT
  const effectiveWastedFeedKg = dynamicWastedFeedKg != null ? dynamicWastedFeedKg : (ssot?.wastedFeedKg || 0);
  const effectiveFeedCostPerKg = dynamicFeedCostPerKg || ssot?.feedCostPerKg || economicImpact?.feedPrice || 1.50;
  const hasFeedWaste = effectiveWastedFeedKg > 0;
  
  // Show section if economicImpact is available OR if there's feed waste data
  const hasEconomicData = (economicImpact && economicImpact.available) || hasFeedWaste;
  if (!hasEconomicData) return null;

  const fmt = (v) => v != null ? v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';
  
  // Mode-aware display
  const isSignalMode = fd.confidenceMode === 'signal';
  const isEvidenceMode = fd.confidenceMode === 'evidence';
  const isDecisionMode = fd.confidenceMode === 'decision';
  
  // Use finalDecision financial context
  const financialContextLevel = fd.financialContextLevel || 'not_actionable';
  const isNotActionable = financialContextLevel === 'not_actionable' && !fd.showFinancialImpact;
  const isIndicative = financialContextLevel === 'indicative' || isEvidenceMode || isSignalMode;
  const isConfirmed = financialContextLevel === 'confirmed' && isDecisionMode;
  
  // ★ Build avoidable feed cost using DYNAMIC values when available
  // This ensures the Financial Impact section updates in real-time with fish count adjustments
  const avoidableFeedCost = hasFeedWaste ? {
    value: dynamicFeedCostImpact != null ? dynamicFeedCostImpact : Math.round(effectiveWastedFeedKg * effectiveFeedCostPerKg * 100) / 100,
    avoidableFeedKg: effectiveWastedFeedKg,
    feedPrice: effectiveFeedCostPerKg,
  } : (economicImpact?.avoidableFeedCost || null);

  return (
    <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-lg p-4 space-y-3" data-testid="economic-impact-section">
      <h4 className="text-sm font-semibold text-emerald-800 flex items-center gap-2">
        <Coins className="w-4 h-4" />
        {fd.modeLabels?.revenueRiskLabel ? 'Financial Impact' : 'Economic Impact Estimate'}
        {/* Mode-aware confidence badge */}
        <span className={`ml-auto text-[10px] font-medium px-2 py-0.5 rounded-full ${
          isDecisionMode && isConfirmed ? 'bg-emerald-200 text-emerald-800' :
          isEvidenceMode ? 'bg-amber-200 text-amber-800' :
          isSignalMode ? 'bg-gray-200 text-gray-600' :
          'bg-gray-200 text-gray-600'
        }`}>
          {isDecisionMode && isConfirmed ? 'Decision Grade' : isEvidenceMode ? 'Indicative' : 'Signal Only'}
        </span>
      </h4>

      {/* SIGNAL MODE: Show explanation instead of precise numbers */}
      {isSignalMode ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
          <p className="text-sm text-gray-600 italic">
            Financial estimates not available in signal-only mode. Detection confidence is too low to provide actionable cost figures.
          </p>
          <p className="text-xs text-gray-500 mt-2">
            To view financial impact estimates, higher detection confidence is required through additional sampling and data validation.
          </p>
        </div>
      ) : isNotActionable ? (
        <p className="text-sm text-gray-500 italic">{fd.financialContextReason || 'Insufficient data reliability for financial estimates.'}</p>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Avoidable Feed Cost — Feed Variance Impact
                PRD economic-language calibration: scale wording with confidence
                so moderate/low confidence figures are not presented as factual
                losses. Formula and value unchanged.
                  • Decision/confirmed → "Estimated avoidable feed cost"
                  • Indicative (evidence-mode / moderate confidence) → "Indicative feed variance exposure"
                  • Otherwise (low confidence) → "Potential feed variance exposure"
              */}
            <VisibilityGate sectionKey="econ.avoidable_feed_cost" label="Avoidable Feed Cost">
            {avoidableFeedCost && (() => {
              const feedCostLabel = (isDecisionMode && isConfirmed)
                ? 'Estimated avoidable feed cost'
                : isIndicative
                  ? 'Indicative feed variance exposure'
                  : 'Potential feed variance exposure';
              return (
              <div className={`rounded-lg border p-3 bg-white border-orange-200`} data-testid="avoidable-feed-cost-card">
                <p className="text-xs font-medium text-orange-700">
                  {feedCostLabel}
                </p>
                {isIndicative && (
                  <p className="text-[9px] text-amber-600 font-medium mb-0.5">Indicative</p>
                )}
                <p className={`text-xl font-bold mt-1 ${isIndicative ? 'text-amber-700' : 'text-orange-900'}`}>
                  {isIndicative ? '~' : ''}{fmt(avoidableFeedCost.value)} EUR
                </p>
                <p className="text-xs text-orange-600 mt-0.5">
                  {isIndicative ? '~' : ''}{avoidableFeedCost.avoidableFeedKg.toLocaleString()} kg feed variance × {fmt(avoidableFeedCost.feedPrice)} EUR/kg
                </p>
              </div>
              );
            })()}
            </VisibilityGate>
            
            {/* Revenue Risk — mode-aware label */}
            <VisibilityGate sectionKey="econ.revenue_risk" label="Revenue Risk">
            <div className={`rounded-lg border p-3 ${economicImpact?.revenueRisk ? 'bg-white border-red-200' : 'bg-gray-50 border-gray-200'}`} data-testid="revenue-risk-card">
              <p className="text-xs font-medium text-red-700">
                {fd.modeLabels?.revenueRiskLabel || (fd.canUseMissingFishWording ? 'Missing Fish Revenue Risk' : 'Deviation Revenue Impact')}
              </p>
              {economicImpact?.revenueRisk ? (
                <>
                  {isIndicative && (
                    <p className="text-[9px] text-amber-600 font-medium mb-0.5">Indicative</p>
                  )}
                  <p className={`text-xl font-bold mt-1 ${isIndicative ? 'text-amber-700' : 'text-red-900'}`}>
                    {fd.financialImpactDisplay || `${isIndicative ? '~' : ''}${fmt(economicImpact.revenueRisk.value)} EUR`}
                  </p>
                  {(isDecisionMode || isIndicative) && (
                    <p className="text-xs text-red-600 mt-0.5">
                      {isIndicative ? '~' : ''}{economicImpact.revenueRisk.missingFish.toLocaleString()} fish x {fmt(economicImpact.revenueRisk.targetWeight)} kg x {fmt(economicImpact.revenueRisk.fishPrice)} EUR/kg
                    </p>
                  )}
                </>
              ) : (
                <p className="text-xs text-gray-500 mt-1">
                  {!economicImpact?.fishPrice || !economicImpact?.targetWeight
                    ? 'Provide fish price & target harvest weight'
                    : (fd.confidenceMode === 'signal' 
                      ? 'Insufficient data for revenue risk estimate'
                      : (fd.modeLabels?.noEvidenceLabel || 'Revenue risk not applicable'))
                  }
                </p>
              )}
            </div>
            </VisibilityGate>
          </div>

          {/* Total Impact Summary */}
          <VisibilityGate sectionKey="econ.total_impact" label="Total Impact">
          {(economicImpact?.revenueRisk || avoidableFeedCost) && (
            <div className={`border rounded-lg p-3 ${
              isIndicative ? 'bg-amber-50 border-amber-300' : 'bg-white border-emerald-300'
            }`} data-testid="total-economic-impact">
              <div className="flex items-center justify-between">
                <span className={`text-sm font-medium ${isIndicative ? 'text-amber-800' : 'text-emerald-800'}`}>
                  {isIndicative ? 'Indicative Total Impact' : 'Total Estimated Impact'}
                </span>
                <span className={`text-lg font-bold ${isIndicative ? 'text-amber-900' : 'text-emerald-900'}`}>
                  {isIndicative ? '~' : ''}{fmt(
                    (economicImpact?.revenueRisk?.value || 0) + (avoidableFeedCost?.value || 0)
                  )} EUR
                </span>
              </div>
              <p className="text-[10px] text-slate-500 mt-1" data-testid="economic-assumptions">
                Based on: €{fmt(effectiveFeedCostPerKg)}/kg feed{economicImpact?.fishPrice ? `, €${fmt(economicImpact.fishPrice)}/kg fish` : ''}
              </p>
            </div>
          )}
          </VisibilityGate>
          
          {/* Delayed Detection Cost Details */}
          <VisibilityGate sectionKey="econ.delayed_detection_cost" label="Delayed Detection Cost">
          {economicImpact?.delayedDetectionCost && (
            <div className="text-xs text-gray-600 bg-gray-50 rounded p-2 mt-2">
              <p className="font-medium text-gray-700 mb-1">Feed Variance Details:</p>
              <p>Avoidable feed: {isIndicative ? '~' : ''}{economicImpact.delayedDetectionCost.avoidableFeedKg.toLocaleString()} kg</p>
              <p>Cost at {fmt(economicImpact.delayedDetectionCost.feedPrice)} EUR/kg: {isIndicative ? '~' : ''}{fmt(economicImpact.delayedDetectionCost.avoidableFeedCostEur)} EUR</p>
              {economicImpact.delayedDetectionCost.startDate && (
                <p>Earliest actionable date: {economicImpact.delayedDetectionCost.startDate}</p>
              )}
            </div>
          )}
          </VisibilityGate>
          
          <VisibilityGate sectionKey="econ.financial_context" label="Financial Context">
          {fd.financialContextReason && (
            <p className="text-xs text-amber-600 italic">{fd.financialContextReason}</p>
          )}
          </VisibilityGate>
        </>
      )}
    </div>
  );
};

/**
 * Detection Methods Reconciliation Card — compact summary comparing all three
 * detection methods using their existing outputs only.
 */
const DetectionReconciliationCard = ({ fcrBasedDetection, trajectoryJump, growthFit, methodResolution, ssot, overriddenOutput = null }) => {
  const fd = overriddenOutput?.finalDecision || {};
  const activeMethod = ssot?.primaryMethod || methodResolution?.activeMethod || 'fcr';
  // ★ MASTER GATE: when missingFishUserVisible is false, suppress all estimates
  const showEstimates = fd.hasMissingFish !== false && fd.confidenceMode !== 'signal';
  // ★ CONFIDENCE MODE: drives detection strength label
  const confidenceMode = fd.confidenceMode || 'signal';
  // ★ SSOT: overall detection strength drives the card's primary badge — mode-aware
  const detectionStrength = fd.detectionStrengthLabel || (
    confidenceMode === 'signal' ? 'Indication Only' : 
    confidenceMode === 'evidence' ? (ssot?.detectionStrength || 'Moderate') + ' (Indicative)' :
    (!showEstimates ? 'N/A' : (ssot?.detectionStrength || 'Low'))
  );
  const methods = [];

  // FCR-based — use SSOT as primary source when FCR is the active method
  if (fcrBasedDetection) {
    const fcrDetected = fcrBasedDetection.detected || (methodResolution?.fcrAvailable);
    const isFcrPrimary = activeMethod === 'fcr';

    let fcrEstimate = null;
    if (showEstimates && fcrDetected) {
      if (isFcrPrimary && ssot?.missingFishCount > 0) {
        fcrEstimate = ssot.missingFishCount;
      } else if (fcrBasedDetection.estimatedMissingFish) {
        fcrEstimate = fcrBasedDetection.estimatedMissingFish;
      } else if (methodResolution?.fcrFishNumber && fcrBasedDetection.reportedFishCount) {
        fcrEstimate = Math.max(0, fcrBasedDetection.reportedFishCount - methodResolution.fcrFishNumber);
      }
    }

    methods.push({
      label: 'FCR-Based',
      detected: showEstimates ? fcrDetected : false,
      estimate: fcrEstimate || null,
      isActive: isFcrPrimary,
      neutralLabel: !showEstimates && fcrDetected ? 'FCR deviation observed' : null,
      // PRD UX-fix #6: when FCR-Based is shown but produces no actionable
      // estimate, surface a calibrated caption rather than a bare "—" cell.
      explanation: !fcrDetected && showEstimates ? 'No confirmed FCR-based deviation signal' : null,
    });
  }

  // Jump-based
  if (trajectoryJump) {
    const jumpDetected = trajectoryJump.jump_event_detected || trajectoryJump.trajectory_jump_detected;
    const jumpConfirmed = trajectoryJump.jump_event_confirmed || trajectoryJump.trajectory_jump_confirmed;
    const estMissing = trajectoryJump.estimated_missing_fish || trajectoryJump.trajectory_based_missing_fish_estimate;
    const isAboveModel = trajectoryJump.jump_above_model !== false;

    const jumpNotActive = activeMethod === 'fcr' && !isAboveModel;
    const isLowQuality = trajectoryJump.estimation_quality === 'poor' || (trajectoryJump.gap_days && trajectoryJump.gap_days > 120);
    const jumpIsConfirmed = !!(jumpDetected && jumpConfirmed && !isLowQuality) && !jumpNotActive;

    methods.push({
      label: 'Jump-Based',
      detected: showEstimates ? jumpIsConfirmed : false,
      estimate: showEstimates && jumpIsConfirmed ? estMissing : null,
      notUsedReason: jumpNotActive
        ? (fd.growthDirectionLabel ? `Not active \u2014 ${fd.growthDirectionLabel}` : 'Not active')
        : (jumpDetected && !jumpConfirmed)
          ? 'Pending confirmation'
          : isLowQuality && jumpDetected
            ? 'Indicative only — data too sparse'
            : (!showEstimates && jumpDetected ? 'Indicative signal' : null),
      // PRD UX-fix #6: when no confirmed jump exists and there is no other
      // active "notUsedReason" copy to render, surface a calibrated caption
      // ("No confirmed jump signal") instead of a bare "—" cell so users
      // never see Jump-Based as an empty placeholder.
      explanation: !jumpIsConfirmed && !jumpNotActive && !(jumpDetected && !jumpConfirmed) && !(isLowQuality && jumpDetected) && !(!showEstimates && jumpDetected)
        ? 'No confirmed jump signal'
        : null,
      isActive: activeMethod === 'jump',
      isAboveModel,
    });
  }

  // Growth-fit
  if (growthFit) {
    methods.push({
      label: 'Growth-Fit deviation estimate',
      detected: showEstimates ? growthFit.detected : false,
      estimate: showEstimates && growthFit.detected ? growthFit.estimatedMissing : null,
      // PRD UX-fix #6: when Growth-Fit has no usable estimate (e.g. method
      // didn't trigger because growth tracked normal), surface an explicit
      // "no signal" caption rather than a bare "—" cell.
      explanation: !showEstimates || !growthFit.detected
        ? 'No confirmed deviation signal'
        : null,
      isActive: false,
    });
  }

  if (methods.length === 0) return null;

  // Agreement: how many detecting methods agree there are missing fish
  const detectingMethods = methods.filter(m => m.detected);
  const totalMethods = methods.length;
  let agreement, agreementColor;
  if (!showEstimates) {
    agreement = 'N/A';
    agreementColor = 'text-gray-500 bg-gray-50 border-gray-200';
  } else if (detectingMethods.length === totalMethods && totalMethods > 1) {
    agreement = 'High';
    agreementColor = 'text-red-700 bg-red-50 border-red-200';
  } else if (detectingMethods.length >= 1 && detectingMethods.length < totalMethods) {
    agreement = 'Moderate';
    agreementColor = 'text-amber-700 bg-amber-50 border-amber-200';
  } else if (detectingMethods.length === 0) {
    agreement = 'Low';
    agreementColor = 'text-green-700 bg-green-50 border-green-200';
  } else {
    agreement = detectingMethods.length === 1 && totalMethods === 1 ? 'Moderate' : 'High';
    agreementColor = detectingMethods.length === 1 && totalMethods === 1
      ? 'text-amber-700 bg-amber-50 border-amber-200'
      : 'text-red-700 bg-red-50 border-red-200';
  }

  // SSOT overall detection strength badge color — mode-aware
  const strengthBadgeColor =
    confidenceMode === 'signal' ? 'bg-amber-50 text-amber-600 border-amber-200'
    : confidenceMode === 'evidence' ? 'bg-blue-50 text-blue-600 border-blue-200'
    : !showEstimates ? 'bg-gray-100 text-gray-500 border-gray-200'
    : detectionStrength === 'High' || detectionStrength.includes('High') ? 'bg-red-100 text-red-700 border-red-200'
    : detectionStrength === 'Moderate' || detectionStrength.includes('Moderate') ? 'bg-amber-100 text-amber-700 border-amber-200'
    : 'bg-green-100 text-green-700 border-green-200';

  return (
    <div className={`rounded-lg border p-3 ${agreementColor}`} data-testid="detection-reconciliation-card">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Crosshair className="w-4 h-4" />
          <span className="text-xs font-semibold">Detection Methods</span>
          <VisibilityGate sectionKey="recon.detection_strength_badge" label="Detection Strength Badge">
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${strengthBadgeColor}`} data-testid="detection-strength-badge">
              Detection Strength: {detectionStrength}
            </span>
          </VisibilityGate>
        </div>
        <VisibilityGate sectionKey="recon.agreement_status" label="Agreement Status">
          <span className="text-xs font-semibold px-2 py-0.5 rounded-full border" data-testid="agreement-status">
            Agreement: {agreement}
          </span>
        </VisibilityGate>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {methods.map((m) => (
          <VisibilityGate key={m.label} sectionKey={`recon.${m.label.toLowerCase().replace(/[^a-z]/g, '_')}`} label={`${m.label} Method`}>
            <div className={`rounded-md px-2 py-1.5 text-center ${m.isActive ? 'bg-white/80 ring-1 ring-indigo-400' : 'bg-white/60'}`}>
              <p className={`text-xs font-medium ${m.isActive ? 'text-indigo-700' : 'text-gray-500'}`}>
                {m.label}
                {m.isActive && <span className="ml-1 text-xs font-semibold text-indigo-500">PRIMARY</span>}
              </p>
              {m.neutralLabel ? (
                <p className="text-xs text-gray-500 mt-0.5">{m.neutralLabel}</p>
              ) : m.notUsedReason ? (
                <p className="text-xs text-gray-400 italic mt-0.5">{m.notUsedReason}</p>
              ) : m.detected && m.estimate != null ? (
                <>
                  <p className="text-[10px] text-gray-500 mt-0.5">deviation estimate</p>
                  <p className="text-sm font-bold text-gray-900">
                    {confidenceMode === 'evidence' ? '~' : ''}{m.estimate.toLocaleString()}
                  </p>
                </>
              ) : m.detected ? (
                <p className="text-xs text-amber-600 font-medium mt-0.5">Signal detected</p>
              ) : m.explanation ? (
                <p className="text-xs text-gray-500 italic mt-0.5">{m.explanation}</p>
              ) : (
                <p className="text-xs text-gray-400 italic mt-0.5">—</p>
              )}
            </div>
          </VisibilityGate>
        ))}
      </div>
      
      {/* ★ CONFLICT/CONSISTENCY NOTICE — when methods have different conclusions */}
      {agreement === 'Moderate' && detectingMethods.length > 0 && detectingMethods.length < totalMethods && (
        <div className="mt-2 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
          <p className="text-[10px] text-amber-800">
            <strong>Note:</strong> Detection methods show different conclusions. The primary method ({activeMethod.toUpperCase()}) result is used for estimates. 
            {fd.flags?.hasConflict && ' Validation recommended before operational decisions.'}
          </p>
        </div>
      )}
    </div>
  );
};

/**
 * Missing Fish Intelligence Report Component
 * Reusable component for displaying the intelligence analysis results
 */
export const MissingFishIntelligenceReport = ({ 
  data, 
  economicImpact = null,
  dataQuality = null,
  mortalityCheck = null,
  samplingRecommendation = null,
  biomassRange = null,
  overfeeding = null,
  trajectoryJump = null,
  fcrBasedDetection = null,
  growthFit = null,
  methodResolution = null,
  sharedMetrics = null,
  ssot = null,
  overriddenOutput = null,  // ★ SSOT-enforced narrative output
  showDeviationTable = true,
  showExplanations = true,
  onShowAllDataChange = null,   // ★ Master override callback to parent
  showAllDataOverride = false,  // ★ Master override state from parent
  dynamicFeedVariance = null,   // ★ Dynamic feed variance values from fish count adjustment
  excludedSampleIndices = new Set(),  // ★ User-controlled sample exclusion indices
  onToggleSampleExclusion = null,     // ★ Callback to toggle sample exclusion
  className = ''
}) => {
  // Use parent-controlled state if available, otherwise local state for backwards compat
  const [localShowAllData, setLocalShowAllData] = useState(false);
  const showAllDataAcknowledged = onShowAllDataChange ? showAllDataOverride : localShowAllData;
  const setShowAllDataAcknowledged = (val) => {
    if (onShowAllDataChange) onShowAllDataChange(val);
    else setLocalShowAllData(val);
  };

  const { isAdmin, resetToDefaults } = useReportVisibility();
  
  if (!data) return null;

  // ── Normalize incoming data: ensure all expected array/object fields have safe defaults ──
  const safeWarn = (field) => {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[MissingFishIntelligenceReport] Expected field "${field}" is missing or not an array — using empty default.`);
    }
  };
  const safeArray = (val, field) => {
    if (Array.isArray(val)) return val;
    if (val !== undefined && val !== null) safeWarn(field);
    return [];
  };

  const explanations = safeArray(data.explanations, 'explanations');
  const deviations = safeArray(data.deviations, 'deviations');
  const analyticalSignals = data.analyticalSignals || {};
  const incorrectSamples = analyticalSignals.incorrectSamples || {};
  const incorrectSamplesList = safeArray(incorrectSamples.samples, 'analyticalSignals.incorrectSamples.samples');
  const dataQualityWarnings = safeArray(analyticalSignals.dataQualityWarnings, 'analyticalSignals.dataQualityWarnings');

  // SSOT drives all canonical labels — fall back to methodResolution if not provided
  const canonicalSSot = ssot || data.ssot || {};
  const activeMethod = canonicalSSot.primaryMethod || methodResolution?.activeMethod || 'fcr';
  const primaryMethodLabel = canonicalSSot.primaryMethodLabel || (activeMethod === 'jump' ? 'Jump-Based' : 'FCR-Based');
  
  // ★ Decision Context from data (generated by generateMissingFishIntelligence)
  const decisionContext = data.decisionContext || null;
  
  // ★ SINGLE DECISION OBJECT — the only source of ALL interpretation
  const fd = overriddenOutput?.finalDecision || {};
  
  // ★ MASTER OVERRIDE: When user acknowledges, treat as 'decision' mode — full data, no guardrails
  // Use _originalConfidenceMode if available (set by parent when override is active)
  const originalConfidenceMode = fd._originalConfidenceMode || fd.confidenceMode;
  const isNonDecisionMode = originalConfidenceMode && originalConfidenceMode !== 'decision';
  const effectiveConfidenceMode = showAllDataAcknowledged && isNonDecisionMode
    ? 'decision' 
    : fd.confidenceMode;
  
  // Create modified finalDecision for child components when override is active
  // ★ When override is active, compute missing fish display values from SSOT
  const ssotMissingFishCount = ssot?.missingFishCount || fd.missingFishCount || 0;
  const ssotAdjustedFishCount = ssot?.adjustedFishCount || 0;
  
  const effectiveFd = showAllDataAcknowledged && isNonDecisionMode
    ? {
        ...fd,
        _originalConfidenceMode: originalConfidenceMode, // Preserve for nested components
        confidenceMode: 'decision',
        confidenceModeLabel: 'Full Data (Override)',
        missingFishUserVisible: true,
        hasMissingFish: fd.hasMissingFish !== false ? true : fd.hasMissingFish,
        showFinancialImpact: true,
        financialIsIndicative: false,
        canUseMissingFishWording: true,
        financialContextLevel: fd._originalFinancialContextLevel || fd.financialContextLevel || 'confirmed',
        financialContextReason: null,
        flags: { ...fd.flags, validationRequired: false, notDecisionGrade: false },
        // ★ Force missing fish count display when override is active
        missingFishCount: ssotMissingFishCount,
        missingFishCountDisplay: ssotMissingFishCount > 0 ? `~${ssotMissingFishCount.toLocaleString()} (Indicative)` : null,
        adjustedFishCountLabel: ssotAdjustedFishCount > 0 ? `~${ssotAdjustedFishCount.toLocaleString()} (Indicative)` : fd.adjustedFishCountLabel,
        modeLabels: {
          ...fd.modeLabels,
          estimatedMissingFishLabel: 'Estimated Missing Fish (Indicative)',
          revenueRiskLabel: 'Missing Fish Revenue Risk',
          reportTitle: 'FCR Signal Analysis',
        }
      }
    : fd;

  // Create modified overriddenOutput for child components when override is active
  const effectiveOverriddenOutput = showAllDataAcknowledged && isNonDecisionMode
    ? { ...overriddenOutput, finalDecision: effectiveFd }
    : overriddenOutput;

  return (
    <div className={`space-y-4 ${className}`} data-testid="missing-fish-intelligence-results">
      {/* PDF Section: Header */}
      <div data-pdf-section="mfi-header">
        <h3 className="text-lg font-semibold text-purple-800 flex items-center gap-2">
          <Brain className="w-5 h-5" />
          {effectiveFd.modeLabels?.reportTitle || 'FCR Signal Analysis'}
          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-purple-100 text-purple-700" data-testid="report-active-method">
            {primaryMethodLabel}
          </span>
          {effectiveFd.confidenceMode && (
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
              showAllDataAcknowledged && isNonDecisionMode ? 'bg-green-100 text-green-700' :
              effectiveFd.confidenceMode === 'signal' ? 'bg-amber-100 text-amber-700' :
              effectiveFd.confidenceMode === 'evidence' ? 'bg-blue-100 text-blue-700' :
              'bg-green-100 text-green-700'
            }`} data-testid="report-confidence-mode">
              {showAllDataAcknowledged && isNonDecisionMode ? 'Full Data (Override)' : effectiveFd.confidenceModeLabel}
            </span>
          )}
        </h3>
      </div>

      {/* Signal Banner — ALWAYS visible when confidence is not decision-grade */}
      {fd.confidenceMode && fd.confidenceMode !== 'decision' && (
        <div className={`border rounded-lg px-4 py-3 ${
          showAllDataAcknowledged ? 'bg-green-50 border-green-300' :
          fd.confidenceMode === 'signal' ? 'bg-amber-50 border-amber-300' : 'bg-blue-50 border-blue-300'
        }`} data-testid="confidence-mode-banner" data-pdf-section="mfi-confidence-warning">
          <div className="flex items-start gap-2">
            <AlertTriangle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
              showAllDataAcknowledged ? 'text-green-600' :
              fd.confidenceMode === 'signal' ? 'text-amber-600' : 'text-blue-600'
            }`} />
            <div className="flex-1">
              <VisibilityGate sectionKey="banner.title" label="Banner Title">
              <p className={`text-sm font-semibold ${
                showAllDataAcknowledged ? 'text-green-800' :
                fd.confidenceMode === 'signal' ? 'text-amber-800' : 'text-blue-800'
              }`}>
                {showAllDataAcknowledged 
                  ? 'View All Data — Active'
                  : (fd.confidenceMode === 'signal' 
                    ? 'Signal Only — Limited Data'
                    : 'Evidence Mode — Indicative Estimates')
                }
              </p>
              </VisibilityGate>
              <VisibilityGate sectionKey="banner.body" label="Banner Body Text">
              <p className={`text-xs mt-0.5 ${
                showAllDataAcknowledged ? 'text-green-700' :
                fd.confidenceMode === 'signal' ? 'text-amber-700' : 'text-blue-700'
              }`}>
                {showAllDataAcknowledged 
                  ? 'All confidence safeguards disabled. All data treated as fully confident. Missing fish features fully activated across all sections.'
                  : (fd.confidenceMode === 'signal' 
                    ? 'Detection confidence is limited due to sparse data. Quantitative outputs are indicative signals only — treat estimates with caution and validate before operational decisions. Additional sampling is recommended.'
                    : 'Detection confidence supports indicative estimates only. Numbers shown with "~" prefix. Validation recommended before operational decisions.')
                }
              </p>
              </VisibilityGate>
              {/* Verification Warning — shown when override is active */}
              {showAllDataAcknowledged && (
                <VisibilityGate sectionKey="banner.verification_warning" label="Verification Warning">
                <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-md" data-testid="override-verification-warning">
                  <div className="flex items-start gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-amber-800 font-medium leading-relaxed">
                      This data has not been fully verified. The values shown are based on limited detection confidence and should be treated as indicative only. Do not use for operational decisions without independent validation.
                    </p>
                  </div>
                </div>
                </VisibilityGate>
              )}
              {showAllDataAcknowledged && (
                <button
                  onClick={() => setShowAllDataAcknowledged(false)}
                  className="mt-2 text-xs text-green-700 hover:text-green-900 underline flex items-center gap-1 font-medium"
                  data-testid="disable-override-btn"
                >
                  <Eye className="w-3 h-3" />
                  Restore confidence safeguards
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ★ PERMANENT: Understanding Confidence & Data Quality Key */}
      <VisibilityGate sectionKey="mfi.confidence_key" label="Confidence & Data Quality Key">
        <div data-pdf-section="mfi-confidence-key">
          <ConfidenceDataQualityKey 
            dataQuality={dataQuality}
            finalDecision={effectiveFd}
            ssot={canonicalSSot}
            collapsed={false}
          />
        </div>
      </VisibilityGate>

      {/* PDF Section: Detection Methods Reconciliation */}
      <VisibilityGate sectionKey="mfi.detection_reconciliation" label="Detection Reconciliation">
        <div data-pdf-section="mfi-reconciliation">
          <DetectionReconciliationCard
            fcrBasedDetection={fcrBasedDetection}
            trajectoryJump={trajectoryJump}
            growthFit={growthFit}
            methodResolution={methodResolution}
            ssot={canonicalSSot}
            overriddenOutput={effectiveOverriddenOutput}
          />
        </div>
      </VisibilityGate>

      {/* PDF Section: Growth Intelligence Summary */}
      <VisibilityGate sectionKey="mfi.growth_intelligence_summary" label="Growth Intelligence Summary">
        <div data-pdf-section="mfi-summary">
          <GrowthIntelligenceSummary
            data={data}
            methodResolution={methodResolution}
            sharedMetrics={sharedMetrics}
            fcrBasedDetection={fcrBasedDetection}
            trajectoryJump={trajectoryJump}
            detectionConfidence={data.detectionConfidence}
            dataQuality={dataQuality}
            ssot={canonicalSSot}
            decisionContext={decisionContext}
            overriddenOutput={effectiveOverriddenOutput}
            growthFit={growthFit}
          />
        </div>
      </VisibilityGate>

      {/* ★ SSOT-Only Conflict Notice — renders ONLY from finalDecision */}
      <VisibilityGate sectionKey="mfi.conflict_notice" label="Conflict Notice">
      {effectiveFd.flags?.hasConflict && effectiveFd.interpretation && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-4" data-pdf-section="mfi-conflict-notice">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-800 mb-1">Signal Conflict Detected</p>
              <p className="text-sm text-amber-700">{fd.interpretation}</p>
              {fd.detectionExplanation && (
                <p className="text-xs text-amber-600 mt-2">{fd.detectionExplanation}</p>
              )}
            </div>
          </div>
        </div>
      )}
      </VisibilityGate>

      {/* PDF Section: Suspect Samples */}
      {trajectoryJump?.suspect_samples?.length > 0 && (
        <div className="space-y-2" data-testid="suspect-samples-section" data-pdf-section="mfi-suspect-samples">
          {trajectoryJump.suspect_samples.map((ss, idx) => (
            <div key={idx} className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-300 rounded-lg">
              <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-semibold text-amber-800" data-testid={`suspect-flag-${idx}`}>
                  {ss.flag}
                </p>
                <p className="text-[11px] text-amber-700 mt-0.5">
                  Sample on {ss.sampleDate}: {ss.sampleWeight}g (expected ~{ss.expectedWeight}g from prior trajectory)
                  {ss.reversionWeight != null && ` \u2014 next sample reverted to ${ss.reversionWeight}g`}
                </p>
                <p className="text-[11px] text-amber-600 mt-0.5">{ss.reason}</p>
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Note when all jumps were sampling errors */}
      {trajectoryJump?.jump_event_note && !trajectoryJump?.jump_event_confirmed && trajectoryJump?.suspect_samples?.length > 0 && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg" data-pdf-section="mfi-jump-note">
          <p className="text-xs text-amber-700 leading-relaxed">{trajectoryJump.jump_event_note}</p>
        </div>
      )}

      {/* PDF Section: Data Quality and Indicators */}
      <div data-pdf-section="mfi-quality-indicators">
        {/* Data Quality Indicator */}
        <VisibilityGate sectionKey="mfi.data_quality_indicator" label="Data Quality Indicator">
          {dataQuality && <DataQualityIndicator dataQuality={dataQuality} />}
        </VisibilityGate>

        {/* Mortality Reality Check Advisory */}
        <VisibilityGate sectionKey="mfi.mortality_check" label="Mortality Reality Check">
          {mortalityCheck && <MortalityRealityCheck mortalityCheck={mortalityCheck} />}
        </VisibilityGate>

        {/* Sampling Recommendation — ★ finalDecision ONLY */}
        <VisibilityGate sectionKey="mfi.sampling_recommendation" label="Sampling Recommendation">
          {fd.samplingRecommendation && (
            <SamplingRecommendation finalDecision={fd} />
          )}
        </VisibilityGate>

        {/* Feeding Impact Classification */}
        <VisibilityGate sectionKey="mfi.feeding_impact_classification" label="Feeding Impact Classification">
          {canonicalSSot && <FeedingImpactIndicator ssot={canonicalSSot} overriddenFeedingNote={overriddenOutput?.finalFeedingExplanation} />}
        </VisibilityGate>
        
        {/* Cannibalism Risk Indicator — RFI <50%, temp >21°C, weight <100g */}
        {canonicalSSot && <CannibalismRiskIndicator ssot={canonicalSSot} />}
        
        {/* Method Conflict Warning — FCR vs Jump discrepancy */}
        {canonicalSSot && <MethodConflictWarning ssot={canonicalSSot} />}
      </div>

      {/* PDF Section: Growth-Fit Fish Number Estimate — ★ finalDecision ONLY */}
      <VisibilityGate sectionKey="mfi.growth_fit_analysis" label="Growth Fit Analysis">
      {growthFit?.detected && fd.growthFitVisibility !== 'hidden' && (
        <div className={`border rounded-lg p-4 space-y-3 ${
          fd.flags?.hasConflict || fd.growthFitVisibility === 'indicative'
            ? 'bg-amber-50 border-amber-300'
            : fd.growthFitVisibility === 'supporting'
              ? 'bg-slate-50 border-slate-300'
              : 'bg-indigo-50 border-indigo-300'
        }`} data-testid="growth-fit-detection" data-pdf-section="mfi-growth-fit">
          <div className="flex items-center gap-2">
            <TrendingUp className={`w-5 h-5 ${
              fd.flags?.hasConflict || fd.growthFitVisibility === 'indicative'
                ? 'text-amber-600'
                : fd.growthFitVisibility === 'supporting'
                  ? 'text-slate-600'
                  : 'text-indigo-600'
            }`} />
            <h4 className={`text-sm font-semibold ${
              fd.flags?.hasConflict || fd.growthFitVisibility === 'indicative'
                ? 'text-amber-900'
                : fd.growthFitVisibility === 'supporting'
                  ? 'text-slate-900'
                  : 'text-indigo-900'
            }`}>Growth-Fit deviation estimate</h4>
            {fd.growthFitAssessment && (
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                fd.flags?.hasConflict || fd.growthFitVisibility === 'indicative'
                  ? 'bg-amber-200 text-amber-800'
                  : fd.growthFitVisibility === 'supporting'
                    ? 'bg-slate-200 text-slate-800'
                    : 'bg-indigo-200 text-indigo-800'
              }`}>
                {fd.growthFitAssessment}
              </span>
            )}
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
              fd.flags?.hasConflict ? 'bg-amber-200 text-amber-800' : 'bg-indigo-200 text-indigo-800'
            }`}>
              R² = {growthFit.rSquared}
            </span>
          </div>
          {/* PRD UX-fix #3: when R² is negative, surface a one-line helper so
              users do not interpret the value as a system error. The negative
              fit score is preserved (not hidden) — only the explanation is
              added. */}
          {typeof growthFit.rSquared === 'number' && growthFit.rSquared < 0 && (
            <p className="text-[11px] leading-relaxed text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5" data-testid="gf-negative-r2-helper">
              Negative fit score indicates insufficient or unstable data for reliable model fitting. Treat the deviation estimate as indicative only.
            </p>
          )}

          {/* Raw metrics (allowed) */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-white border border-gray-200 rounded-md p-2.5">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Reported Fish</p>
              <p className="text-sm font-semibold text-slate-800" data-testid="gf-reported-fish">{growthFit.reportedFishNumber.toLocaleString()}</p>
            </div>
            <VisibilityGate sectionKey="growthfit.best_fit_count" label="Best-Fit Fish Count">
            <div className="bg-white border border-gray-200 rounded-md p-2.5">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Best-Fit Fish</p>
              <p className={`text-sm font-bold text-cyan-700`} data-testid="gf-bestfit-fish">
                {growthFit.bestFitFishNumber ? growthFit.bestFitFishNumber.toLocaleString() : '—'}
              </p>
            </div>
            </VisibilityGate>
            <VisibilityGate sectionKey="growthfit.estimated_missing" label="Estimated Missing Fish">
            <div className="bg-white border border-gray-200 rounded-md p-2.5">
              <p className="text-xs text-gray-500 uppercase tracking-wider">{fd.hasMissingFish ? 'Est. Missing' : 'Deviation'}</p>
              {growthFit.estimatedMissing ? (
                <p className={`text-sm font-bold ${
                  fd.confidenceMode === 'signal' ? 'text-amber-600' :
                  fd.growthFitVisibility === 'supporting' ? 'text-slate-600' : 'text-red-600'
                }`} data-testid="gf-missing-fish">
                  {fd.confidenceMode === 'signal' ? '~' : ''}{growthFit.estimatedMissing.toLocaleString()} ({growthFit.missingPercent}%)
                  {fd.confidenceMode === 'signal' && <span className="text-xs ml-1">(indicative)</span>}
                </p>
              ) : (
                <p className="text-sm font-bold text-slate-600" data-testid="gf-missing-fish">—</p>
              )}
            </div>
            </VisibilityGate>
            <VisibilityGate sectionKey="growthfit.fit_quality" label="Fit Quality">
            <div className="bg-white border border-gray-200 rounded-md p-2.5">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Fit Error (RMSE)</p>
              <p className="text-sm font-semibold text-slate-700" data-testid="gf-fit-error">{growthFit.fitError} g</p>
            </div>
            </VisibilityGate>
          </div>

          <VisibilityGate sectionKey="growthfit.explanation" label="Growth Fit Explanation">
          {fd.growthFitExplanation && (
            <p className={`text-xs leading-relaxed ${
              fd.flags?.hasConflict || fd.growthFitVisibility === 'indicative'
                ? 'text-amber-800'
                : fd.growthFitVisibility === 'supporting'
                  ? 'text-slate-700'
                  : 'text-indigo-800'
            }`}>{fd.growthFitExplanation}</p>
          )}
          </VisibilityGate>
        </div>
      )}
      </VisibilityGate>

      {/* PDF Section: Economic Impact — mode-gated: hidden in signal mode */}
      <VisibilityGate sectionKey="mfi.economic_impact" label="Economic Impact">
      {economicImpact && effectiveFd.showFinancialImpact && (
        <div data-pdf-section="mfi-economic-impact">
          <EconomicImpactSection economicImpact={economicImpact} ssot={canonicalSSot} finalDecision={effectiveFd} dynamicFeedVariance={dynamicFeedVariance} />
        </div>
      )}
      </VisibilityGate>

      {/* Analysis Summary — fd.analysisSummary ONLY
          PRD final-pass UX simplification (#5): drop bullets that are pure
          restatements of conclusions already shown in the Decision Context
          Panel / Operational Decision State (e.g. "Below model expectations",
          "Continue monitoring", "Validation required", "In line with model").
          Each surviving bullet must add NEW information. Filter is wording-
          only; the underlying analysisSummary list and SSOT are unchanged. */}
      <VisibilityGate sectionKey="mfi.analysis_summary" label="Analysis Summary">
      {(() => {
        const DUPLICATE_RESTATEMENT_PATTERNS = [
          /^below model expectations\.?$/i,
          /^above model expectations\.?$/i,
          /^in line with model\.?$/i,
          /^below model performance\.?$/i,
          /^continue monitoring\.?$/i,
          /^continue routine monitoring\.?$/i,
          /^validation required\.?$/i,
          /^indicative deviation observed[\s—-]+insufficient data for confirmation\.?$/i,
          /^deviation observed\.?$/i,
          /^insufficient confidence\.?$/i,
          /^monitor only\.?$/i,
        ];
        const isPureRestatement = (text) => {
          const trimmed = (text || '').toString().trim();
          if (!trimmed) return true;
          return DUPLICATE_RESTATEMENT_PATTERNS.some((re) => re.test(trimmed));
        };
        const dedupedSummary = (fd.analysisSummary || []).filter((exp) => !isPureRestatement(exp.text));
        if (!dedupedSummary.length) return null;
        return (
          <div className="bg-white border border-gray-200 rounded-lg p-4" data-pdf-section="mfi-explanations">
            <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              Analysis Summary
            </h4>
            <div className="space-y-2">
              {dedupedSummary.map((exp) => (
                <div 
                  key={exp.rank}
                  className={`flex items-start gap-3 p-2 rounded-lg ${
                    exp.priority === 'high' ? 'bg-red-50 border border-red-200' :
                    exp.priority === 'medium' ? 'bg-amber-50 border border-amber-200' :
                    exp.priority === 'info' ? 'bg-blue-50 border border-blue-200' :
                    'bg-green-50 border border-green-200'
                  }`}
                >
                  <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    exp.priority === 'high' ? 'bg-red-200 text-red-800' :
                    exp.priority === 'medium' ? 'bg-amber-200 text-amber-800' :
                    exp.priority === 'info' ? 'bg-blue-200 text-blue-800' :
                    'bg-green-200 text-green-800'
                  }`}>
                    {exp.rank}
                  </span>
                  <span className="text-sm">{exp.text}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })()}
      </VisibilityGate>

      {/* PDF Section: Per-Sample Deviation Table */}
      <VisibilityGate sectionKey="mfi.deviation_table" label="Deviation Table">
      {showDeviationTable && deviations.length > 0 && (
        <div className="overflow-x-auto" data-pdf-section="mfi-deviation-table">
          <h4 className="text-sm font-semibold text-gray-800 mb-2">Per-Sample Deviation Analysis</h4>
          {excludedSampleIndices.size > 0 && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-2" data-testid="excluded-samples-notice">
              {excludedSampleIndices.size} sample{excludedSampleIndices.size > 1 ? 's' : ''} excluded from analysis — results recalculated without excluded samples
            </p>
          )}
          <table className="w-full text-xs md:text-sm border-collapse" data-testid="deviation-table">
            <thead>
              <tr className="bg-purple-100">
                {onToggleSampleExclusion && (
                  <th className="border border-purple-300 px-2 py-2 text-center w-10">Include</th>
                )}
                <th className="border border-purple-300 px-2 md:px-3 py-2 text-left">Sample Date</th>
                <th className="border border-purple-300 px-2 md:px-3 py-2 text-right">Actual Weight (g)</th>
                <th className="border border-purple-300 px-2 md:px-3 py-2 text-right">Model Weight (g)</th>
                <th className="border border-purple-300 px-2 md:px-3 py-2 text-right">Deviation (%)</th>
                <th className="border border-purple-300 px-2 md:px-3 py-2 text-center">Interpretation</th>
              </tr>
            </thead>
            <tbody>
              {deviations.map((item) => {
                const sampleIdx = item.sample_number - 1;
                const isExcluded = excludedSampleIndices.has(sampleIdx);
                return (
                <tr key={item.sample_number} className={`${isExcluded ? 'bg-gray-100 opacity-50' : 'hover:bg-purple-50'}`}>
                  {onToggleSampleExclusion && (
                    <td className="border border-purple-200 px-2 py-2 text-center">
                      <input
                        type="checkbox"
                        checked={!isExcluded}
                        onChange={() => onToggleSampleExclusion(sampleIdx)}
                        className="w-3.5 h-3.5 rounded border-purple-400 text-purple-600 focus:ring-purple-500 cursor-pointer"
                        data-testid={`sample-exclude-checkbox-${item.sample_number}`}
                        title={isExcluded ? 'Click to restore this sample' : 'Click to exclude this sample'}
                      />
                    </td>
                  )}
                  <td className={`border border-purple-200 px-2 md:px-3 py-2 ${isExcluded ? 'line-through' : ''}`}>{item.sample_date || `Day ${item.days_since_arrival}`}</td>
                  <td className={`border border-purple-200 px-2 md:px-3 py-2 text-right font-medium ${isExcluded ? 'line-through' : ''}`}>{item.actual_weight_g.toFixed(1)}</td>
                  <td className={`border border-purple-200 px-2 md:px-3 py-2 text-right ${isExcluded ? 'line-through' : ''}`}>{item.model_weight_g.toFixed(1)}</td>
                  <td className={`border border-purple-200 px-2 md:px-3 py-2 text-right font-medium ${isExcluded ? 'line-through' : ''} ${
                    !isExcluded && item.deviation_percent > 5 ? 'text-amber-600' :
                    !isExcluded && item.deviation_percent < -5 ? 'text-blue-600' :
                    !isExcluded ? 'text-green-600' : ''
                  }`}>
                    {item.deviation_percent > 0 ? '+' : ''}{item.deviation_percent.toFixed(1)}%
                  </td>
                  <td className="border border-purple-200 px-2 md:px-3 py-2 text-center">
                    {isExcluded ? (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-500">Excluded</span>
                    ) : (
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${item.tagClass}`}>
                        {item.tag}
                      </span>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      </VisibilityGate>

      {/* ======== ENHANCED ANALYTICAL SIGNALS ======== */}
      {/* ★ finalDecision.advancedSignals ONLY for narrative */}
      <VisibilityGate sectionKey="mfi.advanced_signals" label="Advanced Signals">
      {(fd.advancedSignals?.growthPatternLabel || Object.keys(analyticalSignals).length > 0) && (
        <div className="space-y-4 mt-6 border-t border-purple-200 pt-4">
          <h4 className="text-sm font-semibold text-purple-800 flex items-center gap-2">
            <Activity className="w-4 h-4" />
            Advanced Analytical Signals
          </h4>

          {/* Row 1: Pattern & Signal Strength — ★ finalDecision ONLY */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <VisibilityGate sectionKey="adv.growth_pattern" label="Growth Pattern">
            {fd.advancedSignals?.growthPatternLabel && (
              <div className={`border rounded-lg p-3 ${
                fd.flags?.hasConflict 
                  ? 'bg-amber-50 border-amber-200'
                  : 'bg-indigo-50 border-indigo-200'
              }`} data-testid="pattern-classification">
                <div className="flex items-center gap-2 mb-1">
                  <TrendingUp className={`w-4 h-4 ${fd.flags?.hasConflict ? 'text-amber-600' : 'text-indigo-600'}`} />
                  <p className={`text-xs font-semibold ${fd.flags?.hasConflict ? 'text-amber-700' : 'text-indigo-700'}`}>Growth Pattern</p>
                </div>
                <p className={`text-sm font-bold ${fd.flags?.hasConflict ? 'text-amber-800' : 'text-indigo-800'}`}>
                  {fd.advancedSignals.growthPatternLabel}
                </p>
              </div>
            )}
            </VisibilityGate>

            <VisibilityGate sectionKey="adv.signal_strength" label="Signal Strength">
            {fd.advancedSignals?.growthSignalStrengthLabel && (
              <div className={`border rounded-lg p-3 ${
                fd.flags?.hasConflict
                  ? 'bg-amber-50 border-amber-200'
                  : fd.advancedSignals.growthSignalStrengthLabel === 'Strong' 
                    ? 'bg-red-50 border-red-200' 
                    : fd.advancedSignals.growthSignalStrengthLabel === 'Moderate'
                      ? 'bg-amber-50 border-amber-200'
                      : 'bg-green-50 border-green-200'
              }`} data-testid="signal-strength">
                <div className="flex items-center gap-2 mb-1">
                  <Target className="w-4 h-4 text-gray-600" />
                  <p className="text-xs font-semibold text-gray-700">Growth Signal Strength</p>
                </div>
                <div className="flex items-center gap-2">
                  <p className={`text-lg font-bold ${
                    fd.flags?.hasConflict
                      ? 'text-amber-700'
                      : fd.advancedSignals.growthSignalStrengthLabel === 'Strong' 
                        ? 'text-red-700' 
                        : fd.advancedSignals.growthSignalStrengthLabel === 'Moderate'
                          ? 'text-amber-700'
                          : 'text-green-700'
                  }`}>{fd.advancedSignals.growthSignalStrengthLabel}</p>
                  <span className="text-xs text-gray-500">(Score: {fd.advancedSignals.growthSignalStrengthScore}/100)</span>
                </div>
              </div>
            )}
            </VisibilityGate>
          </div>

          {/* Biomass-Density Estimate — ★ finalDecision ONLY for narrative */}
          <VisibilityGate sectionKey="adv.biomass_density" label="Biomass-Density Estimate">
          {analyticalSignals.biomassDensity && analyticalSignals.biomassDensity.available && (
            <div className={`border rounded-lg p-3 ${
              fd.flags?.hasConflict ? 'bg-amber-50 border-amber-200' : 'bg-rose-50 border-rose-200'
            }`} data-testid="biomass-density-estimate">
              <div className="flex items-center gap-2 mb-2">
                <BarChart3 className={`w-4 h-4 ${fd.flags?.hasConflict ? 'text-amber-600' : 'text-rose-600'}`} />
                <p className={`text-xs font-semibold ${fd.flags?.hasConflict ? 'text-amber-700' : 'text-rose-700'}`}>Biomass-Density Estimate</p>
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  fd.flags?.hasConflict
                    ? 'bg-amber-100 text-amber-700'
                    : analyticalSignals.biomassDensity.signalStrength === 'Strong'
                      ? 'bg-red-100 text-red-700'
                      : analyticalSignals.biomassDensity.signalStrength === 'Moderate'
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-green-100 text-green-700'
                }`}>
                  {fd.flags?.hasConflict ? (fd.confidenceLabel || '') : `${analyticalSignals.biomassDensity.signalStrength}`}
                </span>
              </div>
              {/* Raw metrics */}
              <p className={`text-sm font-bold ${fd.flags?.hasConflict ? 'text-amber-800' : 'text-rose-800'}`}>
                {analyticalSignals.biomassDensity.estimatedReductionRange.low}% – {analyticalSignals.biomassDensity.estimatedReductionRange.high}%
              </p>
            </div>
          )}
          </VisibilityGate>

          {/* Incorrect / Erroneous Sample Detection — ★ finalDecision ONLY for narrative */}
          <VisibilityGate sectionKey="adv.incorrect_samples" label="Incorrect Samples">
          {incorrectSamples.detected && incorrectSamplesList.length > 0 && (
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-3" data-testid="incorrect-samples">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                <p className="text-xs font-semibold text-amber-800">Flagged Sample{incorrectSamples.count > 1 ? 's' : ''}</p>
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-200 text-amber-800">
                  {incorrectSamples.count} flagged
                </span>
              </div>
              <div className="space-y-2">
                {incorrectSamplesList.map((s, idx) => (
                  <div key={idx} className="bg-white border border-amber-200 rounded-md p-2.5" data-testid={`incorrect-sample-${s.sampleNumber}`}>
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${
                        s.severity === 'High' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                      }`}>{s.severity}</span>
                      <span className="text-xs font-semibold text-slate-700">
                        Sample {s.sampleNumber} — {s.sampleDate || `Day ${s.daysInWater}`}
                      </span>
                    </div>
                    {/* Raw metrics allowed */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs mb-2">
                      <div>
                        <p className="text-gray-500">Recorded</p>
                        <p className="font-semibold text-red-700">{s.actualWeight} g</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Expected</p>
                        <p className="font-semibold text-slate-700">{s.expectedWeight} g</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Drop</p>
                        <p className="font-semibold text-red-600">{s.dropPercent}%</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Next Recovery</p>
                        <p className="font-semibold text-green-700">+{s.recoveryPercent}%</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          </VisibilityGate>

          {/* Historical Deviation Analysis — ★ finalDecision ONLY for narrative */}
          <VisibilityGate sectionKey="adv.historical_deviation" label="Historical Deviation Analysis">
          {(analyticalSignals.strongestWindow || analyticalSignals.analyticalStrength) && (
            <div className={`border rounded-lg p-3 mt-4 ${
              fd.flags?.hasConflict ? 'bg-amber-50 border-amber-200' : 'bg-violet-50 border-violet-200'
            }`} data-testid="historical-deviation-analysis">
              <div className="flex items-center gap-2 mb-3">
                <Clock className={`w-4 h-4 ${fd.flags?.hasConflict ? 'text-amber-600' : 'text-violet-600'}`} />
                <p className={`text-sm font-semibold ${fd.flags?.hasConflict ? 'text-amber-800' : 'text-violet-800'}`}>Historical Deviation Analysis</p>
                {fd.flags?.hasConflict && fd.confidenceLabel && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-amber-200 text-amber-800">{fd.confidenceLabel}</span>
                )}
              </div>
              
              {/* Raw metrics only — no sentences */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 text-sm">
                {/* Deviation First Detected */}
                <VisibilityGate sectionKey="adv.hist_deviation_first" label="Deviation First Detected">
                {analyticalSignals.divergence?.detected && (
                  <div className="bg-white border border-violet-100 rounded-lg p-2">
                    <p className="text-xs text-violet-600 font-medium mb-1">Deviation first detected</p>
                    <p className="font-semibold text-violet-900 text-sm">
                      {analyticalSignals.divergence.date || `Day ${analyticalSignals.divergence.day}`}
                    </p>
                  </div>
                )}
                </VisibilityGate>
                
                {/* Strongest Deviation Window */}
                <VisibilityGate sectionKey="adv.hist_strongest_window" label="Strongest Deviation Window">
                {analyticalSignals.strongestWindow && (
                  <div className="bg-white border border-violet-100 rounded-lg p-2">
                    <p className="text-xs text-violet-600 font-medium mb-1">Strongest deviation window</p>
                    <p className="font-semibold text-violet-900 text-sm">
                      {analyticalSignals.strongestWindow.avgDeviation > 0 ? '+' : ''}{analyticalSignals.strongestWindow.avgDeviation?.toFixed(1)}%
                    </p>
                    {analyticalSignals.strongestWindow.startSample && (
                      <p className="text-xs text-violet-500">
                        Samples {analyticalSignals.strongestWindow.startSample}–{analyticalSignals.strongestWindow.endSample}
                      </p>
                    )}
                  </div>
                )}
                </VisibilityGate>
                
                {/* Deviation Persistence */}
                <VisibilityGate sectionKey="adv.hist_persistence" label="Deviation Persistence">
                <div className="bg-white border border-violet-100 rounded-lg p-2">
                  <p className="text-xs text-violet-600 font-medium mb-1">Deviation persistence</p>
                  <p className="font-semibold text-violet-900 text-sm">
                    {analyticalSignals.streak?.maxStreak || 0} samples
                  </p>
                </div>
                </VisibilityGate>
                
                {/* Deviation Volatility */}
                <VisibilityGate sectionKey="adv.hist_volatility" label="Deviation Volatility">
                <div className="bg-white border border-violet-100 rounded-lg p-2">
                  <p className="text-xs text-violet-600 font-medium mb-1">Deviation volatility</p>
                  <p className="font-semibold text-violet-900 text-sm">
                    {analyticalSignals.stability?.stability || 'N/A'}
                  </p>
                </div>
                </VisibilityGate>
                
                {/* Analytical Strength Score */}
                <VisibilityGate sectionKey="adv.hist_analytical_strength" label="Analytical Strength Score">
                {analyticalSignals.analyticalStrength && (
                  <div className={`border rounded-lg p-2 ${
                    analyticalSignals.analyticalStrength.score >= 70 
                      ? 'bg-green-50 border-green-200' 
                      : analyticalSignals.analyticalStrength.score >= 50
                        ? 'bg-amber-50 border-amber-200'
                        : 'bg-red-50 border-red-200'
                  }`}>
                    <p className="text-xs text-gray-600 font-medium mb-1">Analytical strength score</p>
                    <p className={`font-bold text-lg ${
                      analyticalSignals.analyticalStrength.score >= 70 
                        ? 'text-green-700' 
                        : analyticalSignals.analyticalStrength.score >= 50
                          ? 'text-amber-700'
                          : 'text-red-700'
                    }`}>
                      {analyticalSignals.analyticalStrength.score} / 100
                    </p>
                  </div>
                )}
                </VisibilityGate>
              </div>
              
              {/* ★ Interpretation — REMOVED: No raw analyticalSignals.analyticalStrength.interpretation */}
              {/* Raw interpretation text disabled — SSOT summary shown above instead */}

              {/* Data Quality Warnings — factual flags, not narrative */}
              <VisibilityGate sectionKey="adv.data_quality_warnings" label="Data Quality Warnings">
              {dataQualityWarnings.length > 0 && (
                <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-2">
                  <p className="text-xs text-amber-700 font-medium mb-1 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> Data Quality Flags
                  </p>
                  <ul className="text-xs text-amber-800 space-y-1">
                    {dataQualityWarnings.map((warning, idx) => (
                      <li key={idx} className="flex items-start gap-1">
                        <span className="text-amber-500">-</span>
                        <span>{warning.message}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              </VisibilityGate>
            </div>
          )}
          </VisibilityGate>
        </div>
      )}
      </VisibilityGate>

      {/* Admin: Reset Visibility to Defaults */}
      {isAdmin && (
        <div className="mt-4 pt-3 border-t border-slate-200 flex justify-end" data-testid="admin-visibility-reset">
          <button
            onClick={resetToDefaults}
            className="text-xs text-slate-500 hover:text-purple-700 flex items-center gap-1 px-3 py-1.5 rounded border border-slate-200 hover:border-purple-300 transition-colors"
            data-testid="reset-visibility-btn"
          >
            <RotateCcw className="w-3 h-3" />
            Reset visibility to defaults
          </button>
        </div>
      )}
    </div>
  );
};

/**
 * Missing Fish Intelligence Button and Report Wrapper
 * Use this component to add the button + report functionality to any page
 */
export const MissingFishIntelligenceSection = ({
  comparisons,
  dailyFeedData = null,
  disabled = false,
  className = '',
  species = null,
}) => {
  const [showReport, setShowReport] = useState(false);
  const [intelligenceData, setIntelligenceData] = useState(null);

  const handleRunAnalysis = () => {
    if (!comparisons || comparisons.length === 0) {
      toast.error('No sample data available for analysis');
      return;
    }

    const results = calculateMissingFishIntelligence(comparisons, dailyFeedData, new Set(), 1, species);
    setIntelligenceData(results);
    setShowReport(true);
    toast.success('Missing Fish Intelligence analysis complete');
  };

  return (
    <div className={className}>
      {/* Button */}
      <div className="flex justify-center">
        <Button
          onClick={handleRunAnalysis}
          disabled={disabled || !comparisons || comparisons.length === 0}
          className="bg-purple-600 hover:bg-purple-700 text-white"
          data-testid="run-missing-fish-intelligence-btn"
        >
          <Brain className="w-4 h-4 mr-2" />
          Missing Fish Intelligence
        </Button>
      </div>

      {/* Report */}
      {showReport && intelligenceData && (
        <div className="mt-6">
          <MissingFishIntelligenceReport 
            data={intelligenceData} 
          />
        </div>
      )}
    </div>
  );
};

export default MissingFishIntelligenceSection;
export { TrajectoryJumpAlert, SampleJumpAnalysisPanel, FCRAnalysisPanel };
